import Phaser from "phaser";
import "./styles.css";
import {
  ABILITY_COLORS,
  ABILITY_NAMES,
  ARENA_HEIGHT,
  ARENA_WIDTH,
  MOTE_BANK_RADIUS,
  PERSONAL_CHALLENGE_COMPLETE_DISPLAY_MS,
  REVIVE_RADIUS,
  REVIVE_SHIELD_DURATION_MS,
  ULTIMATE_COOLDOWNS,
  ULTIMATE_NAMES,
  VIEWPORT_HEIGHT,
  VIEWPORT_WIDTH,
  type AbilityId,
} from "../shared/constants";
import type {
  CombatFxTuple,
  EnemyTuple,
  LootId,
  LootMessage,
  LootOption,
  MatchStage,
  PlayerStateMessage,
  PlayerTuple,
  PowerUpTuple,
  RaidWarningMessage,
  ServerMessage,
  SnapshotMessage,
  SummonTuple,
} from "../shared/protocol";
import {
  canvasPointToArena,
  touchDragDirection,
  viewportPointToWorld,
} from "../shared/touch";
import { consumeUltimateBarrageCharge } from "../shared/rules";

interface JoinConfig {
  name: string;
  ability: AbilityId;
  session: string;
}

interface PlayerView {
  body: Phaser.GameObjects.Arc;
  outline: Phaser.GameObjects.Arc;
  name: Phaser.GameObjects.Text;
  evolution: Phaser.GameObjects.Text;
  level: Phaser.GameObjects.Text;
  rune: Phaser.GameObjects.Text;
  revive: Phaser.GameObjects.Text;
  targetX: number;
  targetY: number;
  ability: AbilityId;
  ghost: boolean;
  reviveProgress: number;
  reviveShieldUntil: number;
  inspiredUntil: number;
  range: number;
}

interface DeathBurst {
  x: number;
  y: number;
  color: number;
  createdAt: number;
}

interface EnemyView {
  body:
    | Phaser.GameObjects.Arc
    | Phaser.GameObjects.Star
    | Phaser.GameObjects.Rectangle;
  healthBg: Phaser.GameObjects.Rectangle;
  health: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text | null;
  rune: Phaser.GameObjects.Text | null;
  targetX: number;
  targetY: number;
  kind: EnemyTuple[1];
  hpRatio: number;
  size: number;
}

interface PowerUpView {
  orb: Phaser.GameObjects.Arc;
  icon: Phaser.GameObjects.Text;
  kind: PowerUpTuple[1];
  expiresAt: number;
}

interface SummonView {
  body: Phaser.GameObjects.Arc;
  outline: Phaser.GameObjects.Arc;
  rune: Phaser.GameObjects.Text;
  targetX: number;
  targetY: number;
  kind: SummonTuple[1];
  size: number;
  expiresAt: number;
}

interface ArenaTouchState {
  pointerId: number | null;
  startClientX: number;
  startClientY: number;
  x: number;
  y: number;
  targetX: number | null;
  targetY: number | null;
}

const dom = {
  join: required<HTMLElement>("join-screen"),
  name: required<HTMLInputElement>("name-input"),
  starterGrid: required<HTMLElement>("starter-grid"),
  joinError: required<HTMLElement>("join-error"),
  hud: required<HTMLElement>("hud"),
  playerHud: required<HTMLElement>("player-hud"),
  stage: required<HTMLElement>("stage-label"),
  round: required<HTMLElement>("round-label"),
  timer: required<HTMLElement>("timer-label"),
  playerCount: required<HTMLElement>("player-count"),
  connection: required<HTMLElement>("connection-pill"),
  healthMeter: required<HTMLElement>("health-meter"),
  healthLabel: required<HTMLElement>("health-label"),
  xpMeter: required<HTMLElement>("xp-meter"),
  xpLabel: required<HTMLElement>("xp-label"),
  levelLabel: required<HTMLElement>("level-label"),
  abilityLabel: required<HTMLButtonElement>("ability-label"),
  bossHud: required<HTMLElement>("boss-hud"),
  bossMeter: required<HTMLElement>("boss-meter"),
  bossPhase: required<HTMLElement>("boss-phase"),
  personalChallenge: required<HTMLElement>("personal-challenge"),
  challengeName: required<HTMLElement>("challenge-name"),
  challengeCopy: required<HTMLElement>("challenge-copy"),
  challengeStatus: required<HTMLElement>("challenge-status"),
  challengeMeter: required<HTMLElement>("challenge-meter"),
  raidUltimate: required<HTMLElement>("raid-ultimate"),
  raidUltimateLabel: required<HTMLElement>("raid-ultimate-label"),
  raidUltimateMeter: required<HTMLElement>("raid-ultimate-meter"),
  raidUltimateButton: required<HTMLButtonElement>("raid-ultimate-button"),
  evolutionChip: required<HTMLElement>("evolution-chip"),
  teamBuffs: required<HTMLElement>("team-buffs"),
  teamBuffDamage: required<HTMLElement>("team-buff-damage"),
  teamBuffHaste: required<HTMLElement>("team-buff-haste"),
  teamBuffVitality: required<HTMLElement>("team-buff-vitality"),
  teamBuffSpeed: required<HTMLElement>("team-buff-speed"),
  teamBuffArtifacts: required<HTMLElement>("team-buff-artifacts"),
  teamBuffTruesight: required<HTMLElement>("team-buff-truesight"),
  raidWarning: required<HTMLElement>("raid-warning"),
  raidWarningKicker: required<HTMLElement>("raid-warning-kicker"),
  raidWarningTitle: required<HTMLElement>("raid-warning-title"),
  raidWarningCopy: required<HTMLElement>("raid-warning-copy"),
  lootPanel: required<HTMLElement>("loot-panel"),
  lootSource: required<HTMLElement>("loot-source"),
  lootCards: required<HTMLElement>("loot-cards"),
  lootProtection: required<HTMLElement>("loot-protection"),
  lootTimer: required<HTMLElement>("loot-timer"),
  classPanel: required<HTMLElement>("class-panel"),
  classCards: required<HTMLElement>("class-cards"),
  classSummary: required<HTMLElement>("class-summary"),
  ghostPanel: required<HTMLElement>("ghost-panel"),
  respawnTimer: required<HTMLElement>("respawn-timer"),
  results: required<HTMLElement>("results-screen"),
  resultTitle: required<HTMLElement>("result-title"),
  resultCopy: required<HTMLElement>("result-copy"),
  specialChip: required<HTMLElement>("special-chip"),
  specialName: required<HTMLElement>("special-name"),
  specialCooldown: required<HTMLElement>("special-cooldown"),
  relicCount: required<HTMLElement>("relic-count"),
  moteCount: required<HTMLElement>("mote-count"),
  moteBank: required<HTMLElement>("mote-bank"),
  overdriveChip: required<HTMLElement>("overdrive-chip"),
  teamSurgeChip: required<HTMLElement>("team-surge-chip"),
  teamComboChip: required<HTMLElement>("team-combo-chip"),
  powerupChip: required<HTMLElement>("powerup-chip"),
  fogOverlay: required<HTMLElement>("fog-overlay"),
  resultRound: required<HTMLElement>("result-round"),
  leaderboardRows: required<HTMLElement>("leaderboard-rows"),
  roundMedals: required<HTMLElement>("round-medals"),
  artifactVotePanel: required<HTMLElement>("artifact-vote-panel"),
  artifactVoteCards: required<HTMLElement>("artifact-vote-cards"),
  resultsCountdown: required<HTMLElement>("results-countdown"),
  readyCount: required<HTMLElement>("ready-count"),
  readyButton: required<HTMLButtonElement>("ready-button"),
  toastStack: required<HTMLElement>("toast-stack"),
  mobileControls: required<HTMLElement>("mobile-controls"),
  touchStick: required<HTMLElement>("touch-stick"),
  touchKnob: required<HTMLElement>("touch-knob"),
  touchBomb: required<HTMLButtonElement>("touch-bomb"),
  touchDash: required<HTMLButtonElement>("touch-dash"),
  versionBanner: required<HTMLElement>("version-banner"),
  versionRefresh: required<HTMLButtonElement>("version-refresh"),
  versionStamp: required<HTMLElement>("version-stamp"),
  arenaMinimap: required<HTMLCanvasElement>("arena-minimap"),
  arenaBoundary: required<HTMLElement>("arena-boundary"),
  arenaBoundaryLabel: required<HTMLElement>("arena-boundary-label"),
  arenaBoundaryDistance: required<HTMLElement>("arena-boundary-distance"),
};

dom.versionStamp.textContent = "v" + __APP_SEMVER__;
dom.versionStamp.title = "4LOOT v" + __APP_SEMVER__ + " · build " + __APP_VERSION__;

const savedName = localStorage.getItem("4loot-name");
if (savedName) dom.name.value = savedName;
dom.name.addEventListener("keydown", (event) => event.stopPropagation());
dom.name.addEventListener("keyup", (event) => event.stopPropagation());

class GameConnection extends EventTarget {
  socket: WebSocket | null = null;
  localId = "";
  session = "";
  serverOffset = 0;
  private desired = false;
  private config: JoinConfig | null = null;
  private reconnectAttempt = 0;

  connect(config: JoinConfig): void {
    this.config = config;
    this.session = config.session;
    this.desired = true;
    this.open();
  }

  disconnect(): void {
    this.desired = false;
    this.config = null;
    this.reconnectAttempt = 0;
    this.localId = "";
    const socket = this.socket;
    this.socket = null;
    if (
      socket &&
      (socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING)
    ) {
      socket.close(1000, "returned to main screen");
    }
    this.setConnectionState("offline");
  }

  send(message: object): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  rememberAbility(ability: AbilityId): void {
    if (this.config) this.config.ability = ability;
  }

  private open(): void {
    if (!this.config) return;
    this.setConnectionState("connecting");
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const params = new URLSearchParams({
      name: this.config.name,
      ability: this.config.ability,
      session: this.session,
      version: __APP_VERSION__,
    });
    if (location.hostname === "127.0.0.1" || location.hostname === "localhost") {
      const localParams = new URLSearchParams(location.search);
      if (localParams.get("mode") === "fast") params.set("mode", "fast");
      const demoSpawn = localParams.get("spawn");
      if (demoSpawn) params.set("spawn", demoSpawn);
    }
    const socket = new WebSocket(
      protocol + "//" + location.host + "/ws?" + params.toString(),
    );
    this.socket = socket;

    socket.addEventListener("open", () => {
      this.reconnectAttempt = 0;
      this.setConnectionState("online");
    });

    socket.addEventListener("message", (event) => {
      if (typeof event.data !== "string") return;
      let message: ServerMessage | { t: "pong"; at: number };
      try {
        message = JSON.parse(event.data) as ServerMessage | { t: "pong"; at: number };
      } catch {
        return;
      }
      if (message.t === "pong") return;
      if (message.t === "welcome") {
        this.localId = message.id;
        this.session = message.session;
        localStorage.setItem("4loot-session", message.session);
      }
      if (message.t === "snapshot") {
        this.serverOffset = message.now - Date.now();
      }
      this.dispatchEvent(new CustomEvent("server", { detail: message }));
    });

    socket.addEventListener("close", () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.setConnectionState("offline");
      if (!this.desired) return;
      const delay = Math.min(5_000, 400 * 2 ** this.reconnectAttempt);
      this.reconnectAttempt += 1;
      window.setTimeout(() => this.open(), delay);
    });

    socket.addEventListener("error", () => {
      if (!this.localId) {
        dom.joinError.textContent =
          "The vault could not be reached. It may be full—trying again…";
      }
    });
  }

  private setConnectionState(state: "connecting" | "online" | "offline"): void {
    dom.connection.textContent = state;
    dom.connection.dataset.state = state;
  }
}

const connection = new GameConnection();
const mobileMovement = { x: 0, y: 0 };
const arenaTouch: ArenaTouchState = {
  pointerId: null,
  startClientX: 0,
  startClientY: 0,
  x: 0,
  y: 0,
  targetX: null,
  targetY: null,
};
const coarsePointer = window.matchMedia("(hover: none), (pointer: coarse)").matches;

class VaultScene extends Phaser.Scene {
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: Record<"up" | "down" | "left" | "right", Phaser.Input.Keyboard.Key>;
  private players = new Map<string, PlayerView>();
  private enemies = new Map<number, EnemyView>();
  private summons = new Map<number, SummonView>();
  private powerUps = new Map<number, PowerUpView>();
  private snapshot: SnapshotMessage | null = null;
  private combatFx = new Map<number, CombatFxTuple>();
  private deathBursts: DeathBurst[] = [];
  private hazardLayer!: Phaser.GameObjects.Graphics;
  private effectLayer!: Phaser.GameObjects.Graphics;
  private arenaBoundaryLayer!: Phaser.GameObjects.Graphics;
  private activeArena: [number, number, number, number] = [
    0,
    0,
    ARENA_WIDTH,
    ARENA_HEIGHT,
  ];
  private lastInputX = 99;
  private lastInputY = 99;
  private lastInputAt = 0;
  private inputSeq = 0;
  private inputX = 0;
  private inputY = 0;
  private localDashUntil = 0;
  private localDashReadyAt = 0;
  private localDashX = 0;
  private localDashY = 0;
  private followedPlayerId = "";

  constructor() {
    super("vault");
  }

  create(): void {
    this.cameras.main.setBackgroundColor("#100d1d");
    this.cameras.main.setBounds(0, 0, ARENA_WIDTH, ARENA_HEIGHT);
    this.cameras.main.centerOn(ARENA_WIDTH / 2, ARENA_HEIGHT / 2);
    this.drawArena();
    this.arenaBoundaryLayer = this.add.graphics().setDepth(2);
    this.drawActiveArenaBoundary();
    this.hazardLayer = this.add.graphics().setDepth(3);
    this.effectLayer = this.add.graphics().setDepth(7);
    this.game.canvas.tabIndex = 0;
    this.game.canvas.addEventListener("pointerdown", () => focusGameControls());

    if (!this.input.keyboard) throw new Error("Keyboard input unavailable");
    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = this.input.keyboard.addKeys(
      {
        up: Phaser.Input.Keyboard.KeyCodes.W,
        down: Phaser.Input.Keyboard.KeyCodes.S,
        left: Phaser.Input.Keyboard.KeyCodes.A,
        right: Phaser.Input.Keyboard.KeyCodes.D,
      },
      false,
    ) as typeof this.wasd;
    const space = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.SPACE,
      false,
    );
    space.on("down", () => {
      this.fireLootBomb();
    });
    const shift = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.SHIFT,
      false,
    );
    shift.on("down", () => {
      this.startLocalDash();
    });
    this.input.keyboard.clearCaptures();

    connection.addEventListener("server", (event) => {
      const message = (event as CustomEvent<ServerMessage>).detail;
      if (message.t === "snapshot") this.applySnapshot(message);
    });
  }

  update(time: number, delta: number): void {
    this.sendInput(time);
    this.predictLocalPlayer(time, delta);
    this.interpolateViews(delta);
    this.drawHazards();
    this.drawCombatEffects(time);
    updateSpecialHud();
  }

  private drawArena(): void {
    const graphics = this.add.graphics();
    graphics.fillStyle(0x100d1d, 1);
    graphics.fillRect(0, 0, ARENA_WIDTH, ARENA_HEIGHT);

    graphics.lineStyle(1, 0x493c68, 0.22);
    for (let x = 0; x <= ARENA_WIDTH; x += 80) {
      graphics.lineBetween(x, 0, x, ARENA_HEIGHT);
    }
    for (let y = 0; y <= ARENA_HEIGHT; y += 80) {
      graphics.lineBetween(0, y, ARENA_WIDTH, y);
    }

    graphics.lineStyle(3, 0x7253b0, 0.2);
    for (let x = VIEWPORT_WIDTH; x < ARENA_WIDTH; x += VIEWPORT_WIDTH) {
      graphics.lineBetween(x, 0, x, ARENA_HEIGHT);
    }
    for (let y = VIEWPORT_HEIGHT; y < ARENA_HEIGHT; y += VIEWPORT_HEIGHT) {
      graphics.lineBetween(0, y, ARENA_WIDTH, y);
    }

    graphics.fillStyle(0xff4f70, 0.055);
    graphics.fillRect(0, 0, ARENA_WIDTH, 34);
    graphics.fillRect(0, ARENA_HEIGHT - 34, ARENA_WIDTH, 34);
    graphics.fillRect(0, 0, 34, ARENA_HEIGHT);
    graphics.fillRect(ARENA_WIDTH - 34, 0, 34, ARENA_HEIGHT);
    graphics.lineStyle(16, 0xf4bc62, 0.22);
    graphics.strokeRoundedRect(12, 12, ARENA_WIDTH - 24, ARENA_HEIGHT - 24, 28);
    graphics.lineStyle(5, 0xff7189, 0.88);
    graphics.strokeRoundedRect(22, 22, ARENA_WIDTH - 44, ARENA_HEIGHT - 44, 22);
    graphics.lineStyle(2, 0xffe3a0, 0.78);
    graphics.strokeRoundedRect(31, 31, ARENA_WIDTH - 62, ARENA_HEIGHT - 62, 18);
  }

  private applyArenaBounds(
    arena: [number, number, number, number],
  ): void {
    if (arena.every((value, index) => value === this.activeArena[index])) return;
    this.activeArena = [...arena];
    const [x, y, width, height] = this.activeArena;
    this.cameras.main.setBounds(x, y, width, height);
    this.drawActiveArenaBoundary();
  }

  private drawActiveArenaBoundary(): void {
    if (!this.arenaBoundaryLayer) return;
    const [x, y, width, height] = this.activeArena;
    this.arenaBoundaryLayer.clear();
    this.arenaBoundaryLayer.fillStyle(0xff4968, 0.12);
    this.arenaBoundaryLayer.fillRect(x, y, width, 26);
    this.arenaBoundaryLayer.fillRect(x, y + height - 26, width, 26);
    this.arenaBoundaryLayer.fillRect(x, y, 26, height);
    this.arenaBoundaryLayer.fillRect(x + width - 26, y, 26, height);
    this.arenaBoundaryLayer.lineStyle(14, 0xff5d79, 0.38);
    this.arenaBoundaryLayer.strokeRoundedRect(
      x + 7,
      y + 7,
      width - 14,
      height - 14,
      22,
    );
    this.arenaBoundaryLayer.lineStyle(4, 0xffd66b, 0.92);
    this.arenaBoundaryLayer.strokeRoundedRect(
      x + 18,
      y + 18,
      width - 36,
      height - 36,
      16,
    );
  }

  private sendInput(time: number): void {
    let x = mobileMovement.x + arenaTouch.x;
    let y = mobileMovement.y + arenaTouch.y;
    const localView = this.players.get(connection.localId);
    if (
      arenaTouch.targetX !== null &&
      arenaTouch.targetY !== null &&
      localView &&
      !localView.ghost
    ) {
      const dx = arenaTouch.targetX - localView.body.x;
      const dy = arenaTouch.targetY - localView.body.y;
      const targetDistance = Math.hypot(dx, dy);
      if (targetDistance <= 24) {
        arenaTouch.targetX = null;
        arenaTouch.targetY = null;
      } else {
        x += dx / targetDistance;
        y += dy / targetDistance;
      }
    }
    if (this.cursors.left.isDown || this.wasd.left.isDown) x -= 1;
    if (this.cursors.right.isDown || this.wasd.right.isDown) x += 1;
    if (this.cursors.up.isDown || this.wasd.up.isDown) y -= 1;
    if (this.cursors.down.isDown || this.wasd.down.isDown) y += 1;

    const length = Math.hypot(x, y) || 1;
    this.inputX = x / Math.max(1, length);
    this.inputY = y / Math.max(1, length);

    if (
      this.inputX !== this.lastInputX ||
      this.inputY !== this.lastInputY ||
      time - this.lastInputAt > 250
    ) {
      this.lastInputX = this.inputX;
      this.lastInputY = this.inputY;
      this.lastInputAt = time;
      connection.send({
        t: "input",
        x: this.inputX,
        y: this.inputY,
        seq: ++this.inputSeq,
      });
    }
  }

  private applySnapshot(snapshot: SnapshotMessage): void {
    this.snapshot = snapshot;
    this.applyArenaBounds(snapshot.arena);
    for (const effect of snapshot.combatFx) {
      if (
        !this.combatFx.has(effect[0]) &&
        effect[1] === "special" &&
        effect[2] === connection.localId
      ) {
        this.cameras.main.shake(220, 0.006);
      }
      if (!this.combatFx.has(effect[0]) && effect[1] === "raid") {
        this.cameras.main.shake(1_100, 0.022);
        flashRaidUltimate();
      }
      this.combatFx.set(effect[0], effect);
    }
    const seenPlayers = new Set<string>();
    for (const tuple of snapshot.players) {
      seenPlayers.add(tuple[0]);
      let view = this.players.get(tuple[0]);
      if (!view) {
        view = this.createPlayerView(tuple);
        this.players.set(tuple[0], view);
      }
      this.updatePlayerView(view, tuple);
    }
    for (const [id, view] of this.players) {
      if (seenPlayers.has(id)) continue;
      destroyPlayerView(view);
      this.players.delete(id);
    }
    this.updateCameraFollow();

    const seenEnemies = new Set<number>();
    for (const tuple of snapshot.enemies) {
      seenEnemies.add(tuple[0]);
      let view = this.enemies.get(tuple[0]);
      if (!view) {
        view = this.createEnemyView(tuple);
        this.enemies.set(tuple[0], view);
      }
      this.updateEnemyView(view, tuple);
    }
    for (const [id, view] of this.enemies) {
      if (seenEnemies.has(id)) continue;
      this.deathBursts.push({
        x: view.body.x,
        y: view.body.y,
        color: enemyColor(view.kind),
        createdAt: this.time.now,
      });
      if (this.deathBursts.length > 60) this.deathBursts.shift();
      destroyEnemyView(view);
      this.enemies.delete(id);
    }

    const seenSummons = new Set<number>();
    for (const tuple of snapshot.summons) {
      seenSummons.add(tuple[0]);
      let view = this.summons.get(tuple[0]);
      if (!view) {
        view = this.createSummonView(tuple);
        this.summons.set(tuple[0], view);
      }
      this.updateSummonView(view, tuple);
    }
    for (const [id, view] of this.summons) {
      if (seenSummons.has(id)) continue;
      this.deathBursts.push({
        x: view.body.x,
        y: view.body.y,
        color: ABILITY_COLORS.necro,
        createdAt: this.time.now,
      });
      destroySummonView(view);
      this.summons.delete(id);
    }

    const seenPowerUps = new Set<number>();
    for (const tuple of snapshot.powerUps) {
      seenPowerUps.add(tuple[0]);
      let view = this.powerUps.get(tuple[0]);
      if (!view) {
        view = this.createPowerUpView(tuple);
        this.powerUps.set(tuple[0], view);
      }
      view.expiresAt = tuple[4];
    }
    for (const [id, view] of this.powerUps) {
      if (seenPowerUps.has(id)) continue;
      view.orb.destroy();
      view.icon.destroy();
      this.powerUps.delete(id);
    }
  }

  private updateCameraFollow(): void {
    const localId = connection.localId;
    const localView = this.players.get(localId);
    if (!localView) {
      if (this.followedPlayerId) this.cameras.main.stopFollow();
      this.followedPlayerId = "";
      return;
    }
    if (this.followedPlayerId === localId) return;
    this.cameras.main.stopFollow();
    this.cameras.main.startFollow(localView.body, true, 0.28, 0.28);
    this.followedPlayerId = localId;
  }

  private createPowerUpView(tuple: PowerUpTuple): PowerUpView {
    const [, kind, x, y, expiresAt] = tuple;
    const color = powerUpColor(kind);
    const rarePickup = kind === "rod" || kind === "barrage";
    const orb = this.add
      .circle(x, y, rarePickup ? 26 : 21, color, 0.22)
      .setStrokeStyle(rarePickup ? 5 : 3, color, 0.95)
      .setDepth(8);
    const icon = this.add
      .text(x, y, powerUpGlyph(kind), {
        fontFamily: "Archivo Black, system-ui, sans-serif",
        fontStyle: "bold",
        fontSize: rarePickup ? "22px" : "18px",
        color: "#fffdf3",
        stroke: "#100d1d",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(9);
    return { orb, icon, kind, expiresAt };
  }

  private createPlayerView(tuple: PlayerTuple): PlayerView {
    const [id, name, x, y, , , level, ability] = tuple;
    const body = this.add
      .circle(x, y, 18, ABILITY_COLORS[ability], 1)
      .setDepth(6);
    const outline = this.add
      .circle(x, y, 23, 0xffffff, 0)
      .setStrokeStyle(id === connection.localId ? 4 : 2, 0xffffff, id === connection.localId ? 1 : 0.2)
      .setDepth(5);
    const nameText = this.add
      .text(x, y - 43, name, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "13px",
        color: "#f8f2ff",
        stroke: "#100d1d",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(8);
    const evolutionText = this.add
      .text(x, y - 27, "", {
        fontFamily: "DM Mono, monospace",
        fontStyle: "bold",
        fontSize: "9px",
        color: "#ffd66b",
        stroke: "#100d1d",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(8)
      .setVisible(false);
    const levelText = this.add
      .text(x + 17, y + 13, String(level), {
        fontFamily: "system-ui, sans-serif",
        fontStyle: "bold",
        fontSize: "10px",
        color: "#100d1d",
        backgroundColor: "#f4bc62",
        padding: { x: 3, y: 1 },
      })
      .setOrigin(0.5)
      .setDepth(9);
    const rune = this.add
      .text(x, y, abilityGlyph(ability), {
        fontFamily: "system-ui, sans-serif",
        fontStyle: "bold",
        fontSize: "16px",
        color: "#160f25",
      })
      .setOrigin(0.5)
      .setDepth(8);
    const revive = this.add
      .text(x, y + 35, "REVIVE", {
        fontFamily: "DM Mono, monospace",
        fontStyle: "bold",
        fontSize: "10px",
        color: "#9fe8ff",
        stroke: "#100d1d",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(9)
      .setVisible(false);

    return {
      body,
      outline,
      name: nameText,
      evolution: evolutionText,
      level: levelText,
      rune,
      revive,
      targetX: x,
      targetY: y,
      ability,
      ghost: false,
      reviveProgress: 0,
      reviveShieldUntil: 0,
      inspiredUntil: 0,
      range: 1,
    };
  }

  private updatePlayerView(view: PlayerView, tuple: PlayerTuple): void {
    const [
      id,
      name,
      x,
      y,
      hp,
      maxHp,
      level,
      ability,
      ghost,
      ,
      reviveProgress,
      range,
      reviveShieldUntil,
      inspiredUntil,
      rodUntil,
      pickupShieldUntil,
      evolutionName,
    ] = tuple;
    view.targetX = x;
    view.targetY = y;
    if (id === connection.localId) {
      const error = Phaser.Math.Distance.Between(view.body.x, view.body.y, x, y);
      const correction = error > 120 ? 1 : 0.2;
      view.body.x = Phaser.Math.Linear(view.body.x, x, correction);
      view.body.y = Phaser.Math.Linear(view.body.y, y, correction);
    }
    view.ability = ability;
    view.ghost = ghost === 1;
    view.reviveProgress = reviveProgress;
    view.reviveShieldUntil = reviveShieldUntil;
    view.inspiredUntil = inspiredUntil;
    view.range = range;
    view.body.setFillStyle(ABILITY_COLORS[ability], ghost ? 0.2 : 1);
    view.body.setScale(0.85 + 0.15 * Math.max(0, hp / maxHp));
    view.outline.setStrokeStyle(
      rodUntil > Date.now() + connection.serverOffset
        ? 7
        : id === connection.localId
          ? 4
          : 2,
      ghost
        ? 0x9fe8ff
        : rodUntil > Date.now() + connection.serverOffset
          ? 0xff7b36
          : pickupShieldUntil > Date.now() + connection.serverOffset
            ? 0x82a7ff
            : 0xffffff,
      id === connection.localId ||
        rodUntil > Date.now() + connection.serverOffset ||
        pickupShieldUntil > Date.now() + connection.serverOffset
        ? 1
        : ghost
          ? 0.45
          : 0.18,
    );
    view.name.setText(name).setAlpha(ghost ? 0.5 : 1);
    view.evolution
      .setText(evolutionName)
      .setVisible(Boolean(evolutionName))
      .setAlpha(ghost ? 0.5 : 1);
    view.level.setText(String(level)).setAlpha(ghost ? 0.5 : 1);
    view.rune.setText(ghost ? "✦" : abilityGlyph(ability)).setAlpha(ghost ? 0.7 : 1);
    view.revive.setText(
      reviveProgress > 0
        ? "REVIVING " + Math.round(reviveProgress * 100) + "%"
        : id === connection.localId
          ? "ALLY: STAND HERE 3s"
          : "REVIVE",
    );
  }

  private createEnemyView(tuple: EnemyTuple): EnemyView {
    const [, kind, x, y, hpRatio, size, disguiseName, disguiseAbility] = tuple;
    const color = enemyColor(kind);
    const body =
      kind === "gelatinous"
        ? this.add
            .rectangle(x, y, size * 2, size * 2, color, 0.82)
            .setDepth(4)
        : kind === "boss" || kind === "miniboss" || kind === "weakpoint"
        ? this.add
            .star(
              x,
              y,
              kind === "boss" ? 12 : kind === "weakpoint" ? 4 : 7,
              size * (kind === "weakpoint" ? 0.42 : 0.68),
              size,
              color,
              1,
            )
            .setDepth(4)
        : this.add
            .circle(
              x,
              y,
              size,
              kind === "imposter" && disguiseAbility
                ? ABILITY_COLORS[disguiseAbility]
                : color,
              1,
            )
            .setDepth(4);
    if (kind === "boss") body.setStrokeStyle(8, 0xffd66b, 0.8);
    if (kind === "weakpoint") body.setStrokeStyle(6, 0xffffff, 0.94);
    if (kind === "miniboss") body.setStrokeStyle(5, 0xf2b0ff, 0.9);
    if (kind === "guardian") body.setStrokeStyle(6, 0x9effdf, 0.9);
    if (kind === "skitter") body.setStrokeStyle(3, 0xf5ff8a, 0.9);
    if (kind === "bulwark") body.setStrokeStyle(6, 0xffd66b, 0.92);
    if (kind === "stalker") body.setStrokeStyle(4, 0xff6a96, 0.92);
    if (kind === "hexweaver") body.setStrokeStyle(4, 0xd68cff, 0.92);
    if (kind === "imposter") body.setStrokeStyle(3, 0xffffff, 0.22);
    if (kind === "arcspinner") body.setStrokeStyle(5, 0x73efff, 0.92);
    if (kind === "veilkeeper") body.setStrokeStyle(5, 0x9d94c8, 0.9);
    if (kind === "mirecaller") body.setStrokeStyle(5, 0xd77cff, 0.9);
    if (kind === "gravemaw") body.setStrokeStyle(6, 0xff9d5c, 0.94);
    if (kind === "nullbell") body.setStrokeStyle(5, 0xf5f1ff, 0.92);
    if (kind === "shardmother") body.setStrokeStyle(6, 0x7cf5ff, 0.94);
    if (kind === "gelatinous") body.setStrokeStyle(8, 0xb5ff78, 0.92);
    const healthBg = this.add
      .rectangle(x, y - size - 12, Math.max(24, size * 1.8), 5, 0x160f25, 0.9)
      .setDepth(6);
    const health = this.add
      .rectangle(
        x,
        y - size - 12,
        Math.max(24, size * 1.8) * hpRatio,
        5,
        kind === "gelatinous"
          ? 0x9bff70
          : kind === "guardian"
          ? 0x62e8c2
          : kind === "miniboss"
            ? 0xe19aff
            : isNamedEnemy(kind)
              ? 0xff9fe2
            : 0xf4bc62,
        1,
      )
      .setOrigin(0, 0.5)
      .setDepth(7);
    const label = isNamedEnemy(kind) || kind === "gelatinous" || kind === "weakpoint"
      ? this.add
          .text(
            x,
            y - size - 25,
            kind === "gelatinous"
              ? "GELATINOUS CUBE · TOUCH = DEATH"
              : kind === "weakpoint"
                ? "BREAK WEAK POINT"
              : kind === "imposter" && disguiseName
                ? disguiseName
                : namedEnemyName(kind),
            {
            fontFamily: "DM Mono, monospace",
            fontStyle: "bold",
            fontSize: kind === "gelatinous" ? "14px" : "11px",
            color:
              kind === "gelatinous"
                ? "#dbffc4"
                : kind === "weakpoint"
                  ? "#fff2a8"
                  : "#fff0fb",
            stroke: "#170d23",
            strokeThickness: 4,
            },
          )
          .setOrigin(0.5)
          .setDepth(8)
      : null;
    const rune =
      kind === "imposter" && disguiseAbility
        ? this.add
            .text(x, y, abilityGlyph(disguiseAbility), {
              fontFamily: "system-ui, sans-serif",
              fontStyle: "bold",
              fontSize: "16px",
              color: "#160f25",
            })
            .setOrigin(0.5)
            .setDepth(8)
        : null;
    return {
      body,
      healthBg,
      health,
      label,
      rune,
      targetX: x,
      targetY: y,
      kind,
      hpRatio,
      size,
    };
  }

  private updateEnemyView(view: EnemyView, tuple: EnemyTuple): void {
    const [
      ,
      kind,
      x,
      y,
      hpRatio,
      size,
      disguiseName,
      disguiseAbility,
      revealedUntil,
    ] = tuple;
    view.targetX = x;
    view.targetY = y;
    view.hpRatio = hpRatio;
    view.size = size;
    if (
      (kind === "boss" || kind === "miniboss" || kind === "weakpoint") &&
      view.body instanceof Phaser.GameObjects.Star
    ) {
      view.body.rotation +=
        kind === "boss" ? 0.025 : kind === "weakpoint" ? 0.085 : 0.055;
    }
    if (kind === "gelatinous") {
      view.body.rotation = Math.sin(this.time.now / 520) * 0.035;
    }
    if (kind === "imposter") {
      const revealed = revealedUntil > Date.now() + connection.serverOffset;
      view.body.setFillStyle(
        revealed || !disguiseAbility
          ? enemyColor("imposter")
          : ABILITY_COLORS[disguiseAbility],
        1,
      );
      view.body.setStrokeStyle(
        revealed ? 5 : 3,
        revealed ? 0xff254f : 0xffffff,
        revealed ? 1 : 0.22,
      );
      view.label?.setText(revealed ? "IMPOSTER — EXPOSED" : disguiseName);
      view.rune
        ?.setText(revealed || !disguiseAbility ? "!" : abilityGlyph(disguiseAbility))
        .setColor(revealed ? "#fff4f6" : "#160f25");
    }
  }

  private createSummonView(tuple: SummonTuple): SummonView {
    const [, kind, x, y, size, , expiresAt] = tuple;
    const body = this.add
      .circle(x, y, size, enemyColor(kind), 0.42)
      .setDepth(5);
    const outline = this.add
      .circle(x, y, size + 4, ABILITY_COLORS.necro, 0.06)
      .setStrokeStyle(3, ABILITY_COLORS.necro, 0.92)
      .setDepth(5);
    const rune = this.add
      .text(x, y, "☠", {
        fontFamily: "system-ui, sans-serif",
        fontStyle: "bold",
        fontSize: Math.max(12, Math.min(20, size * 0.85)) + "px",
        color: "#d8ffe3",
        stroke: "#15301f",
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(6);
    return {
      body,
      outline,
      rune,
      targetX: x,
      targetY: y,
      kind,
      size,
      expiresAt,
    };
  }

  private updateSummonView(view: SummonView, tuple: SummonTuple): void {
    const [, kind, x, y, size, , expiresAt] = tuple;
    view.targetX = x;
    view.targetY = y;
    view.kind = kind;
    view.size = size;
    view.expiresAt = expiresAt;
  }

  private predictLocalPlayer(time: number, delta: number): void {
    const view = this.players.get(connection.localId);
    if (!view || view.ghost) return;
    const dashing = time < this.localDashUntil;
    const directionX = dashing ? this.localDashX : this.inputX;
    const directionY = dashing ? this.localDashY : this.inputY;
    const serverNow = Date.now() + connection.serverOffset;
    const inspiredSpeed =
      playerState && serverNow < playerState.inspiredUntil ? 1.18 : 1;
    const pickupSpeed =
      playerState && serverNow < playerState.rodUntil
        ? 1.85
        : playerState && serverNow < playerState.hasteUntil
          ? 1.35
          : 1;
    const speed =
      190 *
      (playerState?.moveSpeed ?? 1) *
      inspiredSpeed *
      pickupSpeed *
      (dashing ? 3.1 : 1);
    const dt = Math.min(delta, 50) / 1_000;
    const [arenaX, arenaY, arenaWidth, arenaHeight] = this.activeArena;
    view.body.x = Phaser.Math.Clamp(
      view.body.x + directionX * speed * dt,
      arenaX + 24,
      arenaX + arenaWidth - 24,
    );
    view.body.y = Phaser.Math.Clamp(
      view.body.y + directionY * speed * dt,
      arenaY + 24,
      arenaY + arenaHeight - 24,
    );
  }

  private interpolateViews(delta: number): void {
    const playerFactor = 1 - Math.exp(-Math.min(delta, 50) / 78);
    const enemyFactor = 1 - Math.exp(-Math.min(delta, 50) / 92);
    const localView = this.players.get(connection.localId);
    for (const [id, view] of this.players) {
      if (id !== connection.localId) {
        view.body.x = Phaser.Math.Linear(view.body.x, view.targetX, playerFactor);
        view.body.y = Phaser.Math.Linear(view.body.y, view.targetY, playerFactor);
      }
      view.outline.setPosition(view.body.x, view.body.y);
      view.name.setPosition(view.body.x, view.body.y - 43);
      view.evolution.setPosition(view.body.x, view.body.y - 27);
      view.level.setPosition(view.body.x + 17, view.body.y + 13);
      view.rune.setPosition(view.body.x, view.body.y);
      view.revive.setPosition(view.body.x, view.body.y + 35);
      const nearLocal =
        localView !== undefined &&
        Phaser.Math.Distance.Between(
          localView.body.x,
          localView.body.y,
          view.body.x,
          view.body.y,
        ) <= 180;
      view.revive.setVisible(
        view.ghost && (id === connection.localId || nearLocal || view.reviveProgress > 0),
      );
    }
    for (const view of this.enemies.values()) {
      view.body.x = Phaser.Math.Linear(view.body.x, view.targetX, enemyFactor);
      view.body.y = Phaser.Math.Linear(view.body.y, view.targetY, enemyFactor);
      const width = Math.max(24, view.size * 1.8);
      view.healthBg.setPosition(view.body.x, view.body.y - view.size - 12).setSize(width, 5);
      view.health
        .setPosition(view.body.x - width / 2, view.body.y - view.size - 12)
        .setSize(width * view.hpRatio, 5);
      view.label?.setPosition(view.body.x, view.body.y - view.size - 25);
      view.rune?.setPosition(view.body.x, view.body.y);
      const showHealth =
        view.hpRatio < 0.999 ||
        view.kind === "boss" ||
        view.kind === "miniboss" ||
        view.kind === "guardian" ||
        view.kind === "gelatinous" ||
        view.kind === "weakpoint" ||
        isNamedEnemy(view.kind);
      view.healthBg.setVisible(showHealth);
      view.health.setVisible(showHealth);
    }
    const serverNow = Date.now() + connection.serverOffset;
    for (const view of this.summons.values()) {
      view.body.x = Phaser.Math.Linear(view.body.x, view.targetX, enemyFactor);
      view.body.y = Phaser.Math.Linear(view.body.y, view.targetY, enemyFactor);
      view.outline.setPosition(view.body.x, view.body.y);
      view.rune.setPosition(view.body.x, view.body.y);
      const expiryAlpha = Phaser.Math.Clamp(
        (view.expiresAt - serverNow) / 1_500,
        0.18,
        1,
      );
      view.body.setAlpha(0.42 * expiryAlpha);
      view.outline.setAlpha(expiryAlpha);
      view.rune.setAlpha(expiryAlpha);
    }
    for (const [id, view] of this.powerUps) {
      const pulse = 1 + Math.sin(this.time.now / 130 + id) * 0.1;
      const expiryAlpha = Phaser.Math.Clamp(
        (view.expiresAt - serverNow) / 1_500,
        0.2,
        1,
      );
      view.orb.setScale(pulse).setAlpha(expiryAlpha);
      view.icon
        .setScale(view.kind === "rod" ? pulse * 1.08 : pulse)
        .setAlpha(expiryAlpha);
    }
  }

  private drawHazards(): void {
    this.hazardLayer.clear();
    if (!this.snapshot) return;
    const [arenaX, arenaY, arenaWidth, arenaHeight] = this.activeArena;
    const now = Date.now() + connection.serverOffset;
    const [banked, required] = this.snapshot.moteBank;
    const bankProgress = Phaser.Math.Clamp(banked / Math.max(1, required), 0, 1);
    const guardianAlive = this.snapshot.enemies.some((enemy) => enemy[1] === "guardian");
    const bankLocked =
      guardianAlive || this.snapshot.overdriveDrop !== null || now < this.snapshot.overdriveUntil;
    const bankPulse = 0.5 + Math.sin(this.time.now / 260) * 0.5;
    const bankColor = bankLocked ? 0xff7b59 : 0x62e8c2;
    this.hazardLayer.fillStyle(bankColor, 0.035 + bankPulse * 0.018);
    this.hazardLayer.fillCircle(ARENA_WIDTH / 2, ARENA_HEIGHT / 2, MOTE_BANK_RADIUS);
    this.hazardLayer.lineStyle(3, bankColor, 0.36 + bankPulse * 0.16);
    this.hazardLayer.strokeCircle(ARENA_WIDTH / 2, ARENA_HEIGHT / 2, MOTE_BANK_RADIUS);
    if (bankProgress > 0) {
      this.hazardLayer.lineStyle(8, 0xffd66b, 0.9);
      this.hazardLayer.beginPath();
      this.hazardLayer.arc(
        ARENA_WIDTH / 2,
        ARENA_HEIGHT / 2,
        MOTE_BANK_RADIUS - 7,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * bankProgress,
        false,
      );
      this.hazardLayer.strokePath();
    }
    this.hazardLayer.fillStyle(bankColor, 0.72);
    this.hazardLayer.fillCircle(ARENA_WIDTH / 2, ARENA_HEIGHT / 2, 8 + bankPulse * 3);

    if (this.snapshot.teamCombo) {
      const [x, y, size, progress, occupants] = this.snapshot.teamCombo;
      const pulse = 0.5 + Math.sin(this.time.now / 125) * 0.5;
      const color = occupants >= 2 ? 0xffd66b : 0x69e7ff;
      this.hazardLayer.fillStyle(color, 0.07 + pulse * 0.035);
      this.hazardLayer.fillRect(x - size / 2, y - size / 2, size, size);
      this.hazardLayer.lineStyle(6, color, 0.62 + pulse * 0.28);
      this.hazardLayer.strokeRect(x - size / 2, y - size / 2, size, size);
      this.hazardLayer.fillStyle(color, 0.9);
      this.hazardLayer.fillRect(
        x - size / 2,
        y + size / 2 - 12,
        size * progress,
        12,
      );
      this.hazardLayer.lineStyle(2, 0xffffff, 0.65);
      this.hazardLayer.strokeRect(x - 30, y - 30, 60, 60);
    }

    if (this.snapshot.triangleRally) {
      const [zones, progress, occupiedZones, requiredZones] =
        this.snapshot.triangleRally;
      const pulse = 0.5 + Math.sin(this.time.now / 150) * 0.5;
      const complete = occupiedZones >= requiredZones && requiredZones > 0;
      for (const [x, y, size, zoneOccupants] of zones) {
        const color = complete
          ? 0xff8fd1
          : zoneOccupants > 0
            ? 0xffd66b
            : 0x69e7ff;
        const height = size * 0.866;
        const topY = y - (height * 2) / 3;
        const bottomY = y + height / 3;
        this.hazardLayer.fillStyle(color, 0.08 + pulse * 0.045);
        this.hazardLayer.fillTriangle(
          x,
          topY,
          x - size / 2,
          bottomY,
          x + size / 2,
          bottomY,
        );
        this.hazardLayer.lineStyle(7, color, 0.65 + pulse * 0.28);
        this.hazardLayer.strokeTriangle(
          x,
          topY,
          x - size / 2,
          bottomY,
          x + size / 2,
          bottomY,
        );
        const innerSize = size * (0.2 + progress * 0.68);
        const innerHeight = innerSize * 0.866;
        this.hazardLayer.lineStyle(3, 0xffffff, 0.32 + progress * 0.58);
        this.hazardLayer.strokeTriangle(
          x,
          y - (innerHeight * 2) / 3,
          x - innerSize / 2,
          y + innerHeight / 3,
          x + innerSize / 2,
          y + innerHeight / 3,
        );
      }
    }

    for (const [
      ,
      kind,
      x,
      y,
      radius,
      triggerAt,
      expiresAt,
      startAngle,
      angularSpeed,
      width,
      directionX,
      directionY,
      gapCenter,
      gapSize,
    ] of this.snapshot.hazards) {
      if (kind === "army") {
        const active = now >= triggerAt;
        const travelSeconds = Math.max(0, now - triggerAt) / 1_000;
        const vertical = directionX !== 0;
        const wallPosition = vertical
          ? x + directionX * angularSpeed * travelSeconds
          : y + directionY * angularSpeed * travelSeconds;
        const pulse = 0.5 + Math.sin(this.time.now / 80) * 0.5;
        const gapStart = gapCenter - gapSize / 2;
        const gapEnd = gapCenter + gapSize / 2;
        const axisStart = vertical ? arenaY : arenaX;
        const axisEnd = vertical
          ? arenaY + arenaHeight
          : arenaX + arenaWidth;
        const lineColor = active ? 0xff385e : 0xffa052;
        this.hazardLayer.lineStyle(
          active ? width : 14,
          lineColor,
          active ? 0.38 + pulse * 0.18 : 0.42,
        );
        if (vertical) {
          this.hazardLayer.lineBetween(
            wallPosition,
            arenaY,
            wallPosition,
            gapStart,
          );
          this.hazardLayer.lineBetween(
            wallPosition,
            gapEnd,
            wallPosition,
            arenaY + arenaHeight,
          );
        } else {
          this.hazardLayer.lineBetween(
            arenaX,
            wallPosition,
            gapStart,
            wallPosition,
          );
          this.hazardLayer.lineBetween(
            gapEnd,
            wallPosition,
            arenaX + arenaWidth,
            wallPosition,
          );
        }
        for (
          let position = axisStart + 18;
          position < axisEnd;
          position += 36
        ) {
          if (position >= gapStart && position <= gapEnd) continue;
          const dotX = vertical ? wallPosition : position;
          const dotY = vertical ? position : wallPosition;
          this.hazardLayer.fillStyle(lineColor, active ? 0.9 : 0.58);
          this.hazardLayer.fillCircle(dotX, dotY, active ? 9 + pulse * 3 : 7);
          this.hazardLayer.lineStyle(2, 0xffd2da, 0.75);
          this.hazardLayer.strokeCircle(dotX, dotY, active ? 13 : 10);
        }
        this.hazardLayer.lineStyle(7, 0x8dffcf, 0.82 + pulse * 0.16);
        if (vertical) {
          this.hazardLayer.lineBetween(
            wallPosition - 18,
            gapStart,
            wallPosition + 18,
            gapStart,
          );
          this.hazardLayer.lineBetween(
            wallPosition - 18,
            gapEnd,
            wallPosition + 18,
            gapEnd,
          );
        } else {
          this.hazardLayer.lineBetween(
            gapStart,
            wallPosition - 18,
            gapStart,
            wallPosition + 18,
          );
          this.hazardLayer.lineBetween(
            gapEnd,
            wallPosition - 18,
            gapEnd,
            wallPosition + 18,
          );
        }
        continue;
      }
      if (kind === "laser") {
        const active = now >= triggerAt;
        const angle =
          startAngle + angularSpeed * (Math.max(0, now - triggerAt) / 1_000);
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const pulse = 0.5 + Math.sin(this.time.now / 65) * 0.5;
        const beamAlpha = active ? 0.62 + pulse * 0.24 : 0.28;
        this.hazardLayer.lineStyle(
          active ? width * 2 : 7,
          active ? 0x76f4ff : 0xff5c75,
          beamAlpha,
        );
        this.hazardLayer.lineBetween(
          x,
          y,
          x + cos * radius,
          y + sin * radius,
        );
        this.hazardLayer.lineStyle(active ? 7 : 3, 0xffffff, active ? 0.9 : 0.7);
        this.hazardLayer.lineBetween(
          x,
          y,
          x + cos * radius,
          y + sin * radius,
        );
        this.hazardLayer.fillStyle(active ? 0xe8ffff : 0xff8da0, 0.92);
        this.hazardLayer.fillCircle(x, y, active ? 18 : 12);
        continue;
      }
      if (kind === "collapse") {
        const active = now >= triggerAt;
        const warningWindow = Math.max(1, triggerAt - (expiresAt - 3_850));
        const remainingRatio = Phaser.Math.Clamp(
          (triggerAt - now) / Math.max(1_100, warningWindow),
          0,
          1,
        );
        const shownRadius = active
          ? radius
          : radius + (width - radius) * remainingRatio;
        const pulse = 0.5 + Math.sin(this.time.now / 75) * 0.5;
        this.hazardLayer.fillStyle(0xb51f48, active ? 0.3 : 0.08 + pulse * 0.035);
        this.hazardLayer.fillRect(arenaX, arenaY, arenaWidth, arenaHeight);
        this.hazardLayer.fillStyle(0x64f5ba, active ? 0.3 : 0.11);
        this.hazardLayer.fillCircle(x, y, shownRadius);
        this.hazardLayer.lineStyle(
          active ? 12 : 8,
          active ? 0xffffff : 0xffd66b,
          0.82 + pulse * 0.16,
        );
        this.hazardLayer.strokeCircle(x, y, shownRadius);
        this.hazardLayer.lineStyle(3, 0x8dffcf, 0.9);
        this.hazardLayer.strokeCircle(x, y, radius);
        continue;
      }
      if (now < triggerAt) {
        const remaining = Math.max(0, (triggerAt - now) / 1_650);
        const color =
          kind === "pulse" ? 0xca63ff : kind === "blight" ? 0x9a45d8 : 0xff586d;
        this.hazardLayer.fillStyle(color, 0.11 + (1 - remaining) * 0.16);
        this.hazardLayer.fillCircle(x, y, radius);
        this.hazardLayer.lineStyle(
          kind === "blight" ? 7 : 5,
          kind === "pulse" ? 0xe2a2ff : kind === "blight" ? 0xe08cff : 0xff8796,
          0.75,
        );
        this.hazardLayer.strokeCircle(x, y, radius * (0.7 + remaining * 0.3));
      } else if (now < expiresAt) {
        this.hazardLayer.fillStyle(kind === "blight" ? 0xd65cff : 0xffffff, 0.65);
        this.hazardLayer.fillCircle(x, y, radius);
      }
    }
  }

  startLocalDash(): void {
    const now = performance.now();
    const length = Math.hypot(this.inputX, this.inputY);
    if (now < this.localDashReadyAt || length < 0.1) return;
    this.localDashX = this.inputX / length;
    this.localDashY = this.inputY / length;
    this.localDashUntil = now + 180;
    this.localDashReadyAt = now + 1_500;
    connection.send({ t: "dash", seq: ++this.inputSeq });
  }

  fireLootBomb(autoTarget = false): void {
    if (!playerState || playerState.ghost) return;
    const serverNow = Date.now() + connection.serverOffset;
    if (serverNow < playerState.specialReadyAt) return;
    const target = this.lootBombTarget(autoTarget);
    if (playerState.ultimateCharges > 0) {
      const barrage = consumeUltimateBarrageCharge(
        playerState.ultimateCharges,
        serverNow,
        ULTIMATE_COOLDOWNS[playerState.ability],
      );
      playerState.ultimateCharges = barrage.remaining;
      playerState.specialReadyAt = barrage.readyAt;
    } else {
      playerState.specialReadyAt =
        serverNow + ULTIMATE_COOLDOWNS[playerState.ability];
    }
    connection.send({
      t: "special",
      x: Math.round(target.x),
      y: Math.round(target.y),
      seq: ++this.inputSeq,
    });
  }

  private lootBombTarget(autoTarget = false): Phaser.Math.Vector2 {
    const player = this.players.get(connection.localId);
    if (
      player &&
      playerState &&
      ["moon", "frost", "necro", "bard"].includes(playerState.ability)
    ) {
      return new Phaser.Math.Vector2(player.body.x, player.body.y);
    }
    if (autoTarget && player) {
      const nearest = [...this.enemies.values()]
        .map((enemy) => ({
          enemy,
          distance: Phaser.Math.Distance.Between(
            player.body.x,
            player.body.y,
            enemy.body.x,
            enemy.body.y,
          ),
        }))
        .filter((entry) => entry.distance <= 540)
        .sort((a, b) => a.distance - b.distance)[0];
      if (nearest) {
        return new Phaser.Math.Vector2(nearest.enemy.body.x, nearest.enemy.body.y);
      }
    }
    const pointer = this.input.activePointer;
    const target = new Phaser.Math.Vector2(pointer.worldX, pointer.worldY);
    if (!player) return target;
    const offset = target.clone().subtract(new Phaser.Math.Vector2(player.body.x, player.body.y));
    if (offset.length() > 540) offset.setLength(540);
    return offset.add(new Phaser.Math.Vector2(player.body.x, player.body.y));
  }

  private drawCombatEffects(time: number): void {
    this.effectLayer.clear();
    const serverNow = Date.now() + connection.serverOffset;

    for (const [id, effect] of this.combatFx) {
      const [, kind, attackerId, x, y, radius, targets, createdAt] = effect;
      const duration =
        kind === "raid"
          ? 1_300
          : kind === "special"
            ? 700
            : kind === "reaction" || kind === "necro"
              ? 620
              : 340;
      const age = serverNow - createdAt;
      if (age > duration) {
        this.combatFx.delete(id);
        continue;
      }
      const phase = Phaser.Math.Clamp(age / duration, 0, 1);
      const color =
        kind === "raid"
          ? 0xffe16b
          : kind === "reaction"
            ? 0x8dfff2
        : kind === "special"
          ? ABILITY_COLORS[this.players.get(attackerId)?.ability ?? "solar"]
          : kind === "mote"
            ? 0x62e8c2
            : ABILITY_COLORS[kind];
      const alpha = 0.9 * (1 - phase);

      if (kind === "ember" || kind === "raven") {
        this.effectLayer.lineStyle(kind === "raven" ? 3 : 6, color, alpha);
        for (const [targetX, targetY] of targets) {
          this.effectLayer.lineBetween(x, y, targetX, targetY);
          this.effectLayer.fillStyle(color, alpha);
          this.effectLayer.fillCircle(targetX, targetY, 7 + phase * 10);
        }
      } else if (kind === "chain") {
        this.effectLayer.lineStyle(4, color, alpha);
        let fromX = x;
        let fromY = y;
        for (const [targetX, targetY] of targets) {
          this.effectLayer.lineBetween(fromX, fromY, targetX, targetY);
          fromX = targetX;
          fromY = targetY;
        }
      } else if (kind === "necro") {
        if (targets.length) {
          this.effectLayer.lineStyle(4, color, alpha);
          for (const [targetX, targetY] of targets) {
            this.effectLayer.lineBetween(x, y, targetX, targetY);
            this.effectLayer.fillStyle(color, alpha * 0.7);
            this.effectLayer.fillCircle(targetX, targetY, 5 + (1 - phase) * 5);
          }
        } else {
          const shownRadius = radius * (0.25 + phase * 0.75);
          this.effectLayer.fillStyle(color, alpha * 0.12);
          this.effectLayer.fillCircle(x, y, shownRadius);
          this.effectLayer.lineStyle(5, color, alpha);
          this.effectLayer.strokeCircle(x, y, shownRadius);
        }
      } else {
        const shownRadius =
          radius *
          (kind === "special" || kind === "raid"
            ? 0.18 + phase * 0.82
            : 1);
        this.effectLayer.fillStyle(
          color,
          kind === "special" || kind === "raid" ? alpha * 0.18 : alpha * 0.12,
        );
        this.effectLayer.fillCircle(x, y, shownRadius);
        this.effectLayer.lineStyle(
          kind === "raid" ? 22 : kind === "special" ? 8 : 5,
          color,
          alpha,
        );
        this.effectLayer.strokeCircle(x, y, shownRadius);
        for (const [targetX, targetY] of targets) {
          this.effectLayer.fillStyle(color, alpha * 0.9);
          this.effectLayer.fillCircle(targetX, targetY, 5 + (1 - phase) * 7);
        }
      }
    }

    if (this.snapshot) {
      for (const [, x, y, amount, expiresAt] of this.snapshot.healingDrops) {
        const pulse = 0.5 + Math.sin(time / 180 + x) * 0.5;
        const expiryAlpha = Phaser.Math.Clamp((expiresAt - serverNow) / 2_000, 0.2, 1);
        this.effectLayer.fillStyle(0x58e88c, (0.14 + pulse * 0.12) * expiryAlpha);
        this.effectLayer.fillCircle(x, y, 17 + pulse * 4);
        this.effectLayer.fillStyle(0x8dffae, expiryAlpha);
        this.effectLayer.fillRoundedRect(x - 4, y - 11, 8, 22, 3);
        this.effectLayer.fillRoundedRect(x - 11, y - 4, 22, 8, 3);
        if (amount >= 40) {
          this.effectLayer.lineStyle(2, 0xd4ffe0, expiryAlpha);
          this.effectLayer.strokeCircle(x, y, 20 + pulse * 3);
        }
      }

      for (const [, x, y, expiresAt] of this.snapshot.motes) {
        const pulse = 0.5 + Math.sin(time / 130 + x * 0.03) * 0.5;
        const expiryAlpha = Phaser.Math.Clamp((expiresAt - serverNow) / 1_500, 0.18, 1);
        this.effectLayer.fillStyle(0x62e8c2, (0.12 + pulse * 0.09) * expiryAlpha);
        this.effectLayer.fillCircle(x, y, 10 + pulse * 3);
        this.effectLayer.fillStyle(0xf7d77e, expiryAlpha);
        this.effectLayer.fillCircle(x, y, 4 + pulse * 1.5);
        this.effectLayer.lineStyle(1.5, 0xc5fff0, 0.7 * expiryAlpha);
        this.effectLayer.strokeCircle(x, y, 6 + pulse * 2);
      }

      if (this.snapshot.overdriveDrop) {
        const [, x, y, expiresAt] = this.snapshot.overdriveDrop;
        const pulse = 0.5 + Math.sin(time / 105) * 0.5;
        const expiryAlpha = Phaser.Math.Clamp((expiresAt - serverNow) / 1_500, 0.2, 1);
        this.effectLayer.fillStyle(0xff754f, (0.12 + pulse * 0.1) * expiryAlpha);
        this.effectLayer.fillCircle(x, y, 29 + pulse * 8);
        this.effectLayer.lineStyle(5, 0xffd66b, expiryAlpha);
        this.effectLayer.strokeCircle(x, y, 20 + pulse * 6);
        this.effectLayer.fillStyle(0xfff1a8, expiryAlpha);
        this.effectLayer.fillCircle(x, y, 9);
      }

      if (serverNow < this.snapshot.overdriveUntil) {
        const pulse = 0.5 + Math.sin(time / 72) * 0.5;
        this.effectLayer.lineStyle(3, 0xff513f, 0.5 + pulse * 0.28);
        for (const [id, enemy] of this.enemies) {
          const twitch = Math.sin(time / 34 + id * 1.7) * (3 + pulse * 3);
          this.effectLayer.strokeCircle(
            enemy.body.x + twitch,
            enemy.body.y - twitch * 0.4,
            enemy.size + 7 + pulse * 5,
          );
          this.effectLayer.lineBetween(
            enemy.body.x - 12 - twitch,
            enemy.body.y + enemy.size * 0.35,
            enemy.body.x - 2,
            enemy.body.y + enemy.size * 0.1,
          );
          enemy.body.setScale(1 + pulse * 0.1).setAlpha(0.84 + pulse * 0.16);
        }
        this.effectLayer.lineStyle(3, 0xffd66b, 0.5 + pulse * 0.24);
        for (const player of this.players.values()) {
          if (!player.ghost) {
            this.effectLayer.strokeCircle(player.body.x, player.body.y, 27 + pulse * 3);
          }
        }
      } else {
        for (const enemy of this.enemies.values()) {
          enemy.body.setScale(1).setAlpha(1);
        }
      }
    }

    if (currentLoot && currentLoot.source !== "grave") {
      const localPlayer = this.players.get(connection.localId);
      if (localPlayer && !localPlayer.ghost) {
        const pulse = 0.5 + Math.sin(time / 115) * 0.5;
        this.effectLayer.fillStyle(0x69e7ff, 0.08 + pulse * 0.08);
        this.effectLayer.fillCircle(
          localPlayer.body.x,
          localPlayer.body.y,
          31 + pulse * 5,
        );
        this.effectLayer.lineStyle(4, 0x9ff4ff, 0.7 + pulse * 0.25);
        this.effectLayer.strokeCircle(
          localPlayer.body.x,
          localPlayer.body.y,
          27 + pulse * 4,
        );
      }
    }

    if (arenaTouch.targetX !== null && arenaTouch.targetY !== null) {
      const pulse = 0.5 + Math.sin(time / 150) * 0.5;
      this.effectLayer.fillStyle(0x9fe8ff, 0.08 + pulse * 0.05);
      this.effectLayer.fillCircle(arenaTouch.targetX, arenaTouch.targetY, 18 + pulse * 4);
      this.effectLayer.lineStyle(3, 0x9fe8ff, 0.58 + pulse * 0.24);
      this.effectLayer.strokeCircle(arenaTouch.targetX, arenaTouch.targetY, 13 + pulse * 4);
    }

    for (const view of this.players.values()) {
      if (!view.ghost && view.ability === "frost") {
        const auraRadius = 190 * view.range;
        const pulse = 0.5 + Math.sin(time / 240 + view.body.x) * 0.5;
        this.effectLayer.fillStyle(0x78cfff, 0.035 + pulse * 0.025);
        this.effectLayer.fillCircle(view.body.x, view.body.y, auraRadius);
        this.effectLayer.lineStyle(2, 0x78cfff, 0.2 + pulse * 0.12);
        this.effectLayer.strokeCircle(view.body.x, view.body.y, auraRadius);
      }
      if (!view.ghost && serverNow < view.inspiredUntil) {
        const pulse = 0.5 + Math.sin(time / 95 + view.body.x * 0.01) * 0.5;
        this.effectLayer.lineStyle(3, 0xff8fcb, 0.45 + pulse * 0.35);
        this.effectLayer.strokeCircle(view.body.x, view.body.y, 27 + pulse * 5);
        this.effectLayer.lineStyle(2, 0xffd66b, 0.35 + pulse * 0.25);
        this.effectLayer.strokeCircle(view.body.x, view.body.y, 33 + pulse * 4);
      }
      if (!view.ghost && serverNow < view.reviveShieldUntil) {
        const remaining = Phaser.Math.Clamp(
          (view.reviveShieldUntil - serverNow) / REVIVE_SHIELD_DURATION_MS,
          0,
          1,
        );
        const pulse = 0.5 + Math.sin(time / 80) * 0.5;
        this.effectLayer.fillStyle(0x9fe8ff, 0.07 + pulse * 0.05);
        this.effectLayer.fillCircle(view.body.x, view.body.y, 31 + pulse * 3);
        this.effectLayer.lineStyle(3, 0x9fe8ff, 0.55 + pulse * 0.3);
        this.effectLayer.strokeCircle(view.body.x, view.body.y, 31 + pulse * 3);
        this.effectLayer.lineStyle(6, 0xd8f7ff, 0.95);
        this.effectLayer.beginPath();
        this.effectLayer.arc(
          view.body.x,
          view.body.y,
          37,
          -Math.PI / 2,
          -Math.PI / 2 + Math.PI * 2 * remaining,
          false,
        );
        this.effectLayer.strokePath();
      }
      if (!view.ghost) continue;
      this.effectLayer.lineStyle(2, 0x9fe8ff, 0.32);
      this.effectLayer.strokeCircle(view.body.x, view.body.y, REVIVE_RADIUS);
      if (view.reviveProgress <= 0) continue;
      this.effectLayer.lineStyle(7, 0x69e7ff, 0.95);
      this.effectLayer.beginPath();
      this.effectLayer.arc(
        view.body.x,
        view.body.y,
        REVIVE_RADIUS,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * view.reviveProgress,
        false,
      );
      this.effectLayer.strokePath();
    }

    this.deathBursts = this.deathBursts.filter((burst) => {
      const phase = (time - burst.createdAt) / 420;
      if (phase >= 1) return false;
      const alpha = 0.8 * (1 - phase);
      this.effectLayer.lineStyle(3, burst.color, alpha);
      this.effectLayer.strokeCircle(burst.x, burst.y, 8 + phase * 26);
      this.effectLayer.fillStyle(burst.color, alpha);
      for (let index = 0; index < 4; index += 1) {
        const angle = index * (Math.PI / 2) + burst.x * 0.01;
        const distance = 8 + phase * 30;
        this.effectLayer.fillCircle(
          burst.x + Math.cos(angle) * distance,
          burst.y + Math.sin(angle) * distance,
          3,
        );
      }
      return true;
    });

    if (playerState && !playerState.ghost && !coarsePointer) {
      const target = this.lootBombTarget();
      const ready = serverNow >= playerState.specialReadyAt;
      this.effectLayer.lineStyle(2, ready ? 0xffd66b : 0x8b819a, ready ? 0.82 : 0.3);
      this.effectLayer.strokeCircle(target.x, target.y, 165);
      this.effectLayer.lineBetween(target.x - 9, target.y, target.x + 9, target.y);
      this.effectLayer.lineBetween(target.x, target.y - 9, target.x, target.y + 9);
    }
  }
}

const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent: "game-root",
  width: VIEWPORT_WIDTH,
  height: VIEWPORT_HEIGHT,
  transparent: true,
  antialias: true,
  render: {
    pixelArt: false,
    roundPixels: false,
  },
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  scene: [VaultScene],
});

let currentLoot: LootMessage | null = null;
let lootCountdown: number | null = null;
let playerState: PlayerStateMessage | null = null;
let latestSnapshot: SnapshotMessage | null = null;
let classPanelOpen = false;
let lastStage: MatchStage | null = null;
let lastResultsSignature = "";
let deployedVersion = "";
let lastPersonalChallengeKind = "";
let challengeArrivalTimer: number | null = null;
let submittedLootExpiresAt = 0;

dom.starterGrid.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
    "button[data-ability]",
  );
  if (!button) return;

  const name = dom.name.value.trim() || randomName();
  const ability = button.dataset.ability as AbilityId;
  const session = localStorage.getItem("4loot-session") ?? crypto.randomUUID();
  localStorage.setItem("4loot-name", name);
  dom.name.value = name;
  dom.joinError.textContent = "";
  for (const starter of dom.starterGrid.querySelectorAll("button")) {
    starter.toggleAttribute("disabled", true);
  }

  connection.connect({ name, ability, session });
});

connection.addEventListener("server", (event) => {
  const message = (event as CustomEvent<ServerMessage>).detail;

  if (message.t === "welcome") {
    checkAppVersion(message.appVersion);
    dom.join.classList.add("is-hidden");
    dom.hud.classList.remove("is-hidden");
    dom.playerHud.classList.remove("is-hidden");
    dom.arenaMinimap.classList.remove("is-hidden");
    document.body.classList.add("game-active");
    dom.joinError.textContent = "";
    if (coarsePointer) {
      window.setTimeout(
        () => showToast("Tap the arena to move · hold and drag to steer"),
        450,
      );
    }
    window.requestAnimationFrame(() => focusGameControls(true));
    return;
  }

  if (message.t === "snapshot") {
    checkAppVersion(message.appVersion);
    latestSnapshot = message;
    renderSnapshotHud(message);
    return;
  }

  if (message.t === "player") {
    if (playerState && message.hp < playerState.hp && !message.ghost) flashDamage();
    playerState = message;
    syncPendingLoot(message.pendingLoot);
    if (message.ghost) resetTouchMovement();
    renderPlayerHud(message);
    if (latestSnapshot?.roundResults) renderRoundResults(latestSnapshot);
    return;
  }

  if (message.t === "loot") {
    showLoot(message);
    return;
  }

  if (message.t === "raid") {
    showRaidWarning(message);
    return;
  }

  if (message.t === "notice") {
    if (message.kind === "idle") {
      returnToMainScreen(message.text);
    } else if (message.kind === "victory" || message.kind === "defeat") {
      showResults(message.kind, message.text);
    } else {
      showToast(message.text);
    }
  }
});

function returnToMainScreen(message: string): void {
  connection.disconnect();
  hideLoot();
  classPanelOpen = false;
  playerState = null;
  latestSnapshot = null;
  lastStage = null;
  lastResultsSignature = "";
  submittedLootExpiresAt = 0;
  resetTouchMovement();

  if (raidWarningTimer) window.clearTimeout(raidWarningTimer);
  raidWarningTimer = null;
  raidWarningSequence += 1;
  activeRaidWarningKind = null;
  queuedRaidWarning = null;

  dom.hud.classList.add("is-hidden");
  dom.playerHud.classList.add("is-hidden");
  dom.lootPanel.classList.add("is-hidden");
  dom.classPanel.classList.add("is-hidden");
  dom.results.classList.add("is-hidden");
  dom.bossHud.classList.add("is-hidden");
  dom.personalChallenge.classList.add("is-hidden");
  dom.raidUltimate.classList.add("is-hidden");
  dom.teamBuffs.classList.add("is-hidden");
  dom.ghostPanel.classList.add("is-hidden");
  dom.arenaMinimap.classList.add("is-hidden");
  dom.arenaBoundary.classList.add("is-hidden");
  dom.raidWarning.classList.add("is-hidden");
  dom.raidWarning.classList.remove("show");
  dom.fogOverlay.classList.add("is-hidden");
  dom.join.classList.remove("is-hidden");
  dom.joinError.textContent = message;
  for (const starter of dom.starterGrid.querySelectorAll("button")) {
    starter.removeAttribute("disabled");
  }
  for (const toast of document.querySelectorAll(".toast")) toast.remove();
  document.body.classList.remove(
    "game-active",
    "loot-open",
    "class-open",
    "results-open",
    "overdrive-active",
    "team-surge-active",
  );
  window.requestAnimationFrame(() => dom.name.focus());
}

dom.versionRefresh.addEventListener("click", () => {
  if (deployedVersion) {
    localStorage.setItem("4loot-dismissed-version", deployedVersion);
  }
  dom.versionBanner.classList.add("is-hidden");
  document.body.classList.remove("update-available");
  const url = new URL(window.location.href);
  url.searchParams.set(
    "build",
    (deployedVersion || "refresh") + "-" + Date.now(),
  );
  window.location.replace(url);
});

function checkAppVersion(serverVersion: string): void {
  if (!serverVersion || serverVersion <= __APP_VERSION__) return;
  const dismissedVersion = localStorage.getItem("4loot-dismissed-version") ?? "";
  if (serverVersion <= dismissedVersion) return;
  deployedVersion = serverVersion;
  dom.versionBanner.classList.remove("is-hidden");
  document.body.classList.add("update-available");
}

async function pollForAppVersion(): Promise<void> {
  try {
    const response = await fetch(
      "/api/version?loaded=" + encodeURIComponent(__APP_VERSION__),
      { cache: "no-store" },
    );
    if (!response.ok) return;
    const payload = (await response.json()) as { appVersion?: string };
    if (payload.appVersion) checkAppVersion(payload.appVersion);
  } catch {
    // A version check should never interrupt the match or reconnect flow.
  }
}

void pollForAppVersion();
window.setInterval(() => void pollForAppVersion(), 10_000 + Math.random() * 2_000);
window.addEventListener("focus", () => void pollForAppVersion());
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void pollForAppVersion();
});

window.addEventListener("keydown", (event) => {
  if (currentLoot) {
    const index = Number(event.key) - 1;
    if (index >= 0 && index < currentLoot.options.length) {
      chooseLoot(currentLoot.options[index]);
    }
    return;
  }

  if (latestSnapshot?.roundResults?.result === "victory") {
    const index = Number(event.key) - 1;
    const option = latestSnapshot.roundResults.artifactVotes[index]?.option;
    if (option) castVote(option.id);
  }

  if (event.key.toLowerCase() === "c" && playerState && !event.repeat) {
    toggleClassPanel();
  }
});

dom.abilityLabel.addEventListener("click", () => toggleClassPanel());

dom.readyButton.addEventListener("click", () => {
  if (!playerState || !latestSnapshot?.roundResults) return;
  const ready = !playerState.ready;
  connection.send({ t: "ready", ready });
  playerState.ready = ready;
  renderRoundResults(latestSnapshot, true);
});

dom.raidUltimateButton.addEventListener("click", () => {
  if (!playerState || !latestSnapshot?.raidUltimate[1]) return;
  const fire = !playerState.raidUltimateVoted;
  connection.send({ t: "raidUltimateVote", fire });
  playerState.raidUltimateVoted = fire;
  renderRaidUltimate(latestSnapshot);
});

dom.classCards.addEventListener("click", (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
    "button[data-switch-ability]",
  );
  if (!button || !playerState) return;
  switchAbility(button.dataset.switchAbility as AbilityId);
});

function renderSnapshotHud(snapshot: SnapshotMessage): void {
  if (lastStage && snapshot.stage !== lastStage) {
    const callout: Partial<Record<MatchStage, string>> = {
      escalation: "THE VAULT STIRS — MORE MONSTERS INCOMING",
      chaos: "GREED UNBOUND — SURVIVE THE SWARM",
      descent: "A NEW DESCENT BEGINS",
    };
    const text = callout[snapshot.stage];
    if (text) showToast(text);
    if (snapshot.stage === "boss") {
      game.scene.getScene("vault").cameras.main.shake(500, 0.01);
    }
  }
  lastStage = snapshot.stage;
  dom.round.textContent = String(snapshot.round);
  dom.playerCount.textContent = String(snapshot.players.length);
  dom.stage.textContent = stageName(snapshot.stage);
  dom.timer.textContent = formatTime(snapshot.remaining);
  dom.bossHud.classList.toggle("is-hidden", !snapshot.boss);
  if (snapshot.boss) {
    const [hp, maxHp, phase] = snapshot.boss;
    dom.bossMeter.style.width = percentage(hp / maxHp);
    dom.bossPhase.textContent = "PHASE " + phase;
  }
  renderRaidUltimate(snapshot);
  const [bankedMotes, requiredMotes] = snapshot.moteBank;
  dom.moteBank.textContent = bankedMotes + "/" + requiredMotes;
  const overdriveRemaining = Math.max(
    0,
    snapshot.overdriveUntil - (Date.now() + connection.serverOffset),
  );
  dom.overdriveChip.classList.toggle("is-hidden", overdriveRemaining <= 0);
  dom.overdriveChip.textContent =
    "2× DAMAGE · 2× XP · FRENZY " +
    (overdriveRemaining / 1_000).toFixed(1) +
    "s";
  document.body.classList.toggle("overdrive-active", overdriveRemaining > 0);
  const surgeRemaining = Math.max(
    0,
    snapshot.teamSurgeUntil - (Date.now() + connection.serverOffset),
  );
  dom.teamSurgeChip.classList.toggle("is-hidden", surgeRemaining <= 0);
  dom.teamSurgeChip.textContent =
    "△ 2× DAMAGE · 2× XP · " + (surgeRemaining / 1_000).toFixed(1) + "s";
  document.body.classList.toggle("team-surge-active", surgeRemaining > 0);
  if (snapshot.teamCombo) {
    const [, , , progress, occupants, , captureMs] = snapshot.teamCombo;
    const required = snapshot.players.length === 1 ? 1 : 2;
    dom.teamComboChip.classList.remove("is-hidden");
    dom.teamComboChip.textContent =
      "□ TEAM COMBO " +
      occupants +
      "/" +
      required +
      " · " +
      Math.max(0, (captureMs / 1_000) * (1 - progress)).toFixed(1) +
      "s";
  } else {
    dom.teamComboChip.classList.add("is-hidden");
  }
  const [damageBuffs, hasteBuffs, vitalityBuffs, speedBuffs] = snapshot.teamBuffs;
  const truthActive =
    snapshot.teamTrueSightUntil > Date.now() + connection.serverOffset;
  const totalTeamBuffs =
    damageBuffs +
    hasteBuffs +
    vitalityBuffs +
    speedBuffs +
    snapshot.teamArtifacts.length +
    (truthActive ? 1 : 0);
  dom.teamBuffs.classList.toggle("is-hidden", totalTeamBuffs === 0);
  renderTeamBuff(dom.teamBuffDamage, damageBuffs);
  renderTeamBuff(dom.teamBuffHaste, hasteBuffs);
  renderTeamBuff(dom.teamBuffVitality, vitalityBuffs);
  renderTeamBuff(dom.teamBuffSpeed, speedBuffs);
  renderTeamBuff(dom.teamBuffArtifacts, snapshot.teamArtifacts.length);
  const truthCounter = dom.teamBuffTruesight.querySelector("i");
  if (truthCounter) truthCounter.textContent = truthActive ? "ON" : "OFF";
  dom.teamBuffTruesight.classList.toggle("inactive", !truthActive);
  dom.teamBuffArtifacts.title = snapshot.teamArtifactRounds
    .map(
      ([artifact, rounds]) =>
        ABILITY_NAMES[artifact] + " · " + rounds + " round" + (rounds === 1 ? "" : "s"),
    )
    .join("\n");

  const veilActive = snapshot.enemies.some((enemy) => enemy[1] === "veilkeeper");
  const localPlayer = snapshot.players.find((player) => player[0] === connection.localId);
  renderArenaNavigation(snapshot, localPlayer, veilActive);
  dom.fogOverlay.classList.toggle("is-hidden", !veilActive || !localPlayer);
  if (veilActive && localPlayer) {
    const camera = (game.scene.getScene("vault") as VaultScene).cameras.main;
    const canvasBounds = game.canvas.getBoundingClientRect();
    const screenX =
      canvasBounds.left +
      ((localPlayer[2] - camera.worldView.x) / VIEWPORT_WIDTH) *
        canvasBounds.width;
    const screenY =
      canvasBounds.top +
      ((localPlayer[3] - camera.worldView.y) / VIEWPORT_HEIGHT) *
        canvasBounds.height;
    dom.fogOverlay.style.setProperty(
      "--fog-x",
      Phaser.Math.Clamp((screenX / window.innerWidth) * 100, 0, 100).toFixed(2) +
        "%",
    );
    dom.fogOverlay.style.setProperty(
      "--fog-y",
      Phaser.Math.Clamp((screenY / window.innerHeight) * 100, 0, 100).toFixed(2) +
        "%",
    );
  }
  dom.results.classList.toggle("is-hidden", snapshot.stage !== "results");
  document.body.classList.toggle("results-open", snapshot.stage === "results");
  if (snapshot.stage === "results") {
    dom.raidWarning.classList.add("is-hidden");
    dom.raidWarning.classList.remove("show");
    if (currentLoot) hideLoot();
    if (classPanelOpen) hideClassPanel();
    resetTouchMovement();
    renderRoundResults(snapshot);
  } else {
    lastResultsSignature = "";
  }
}

function renderArenaNavigation(
  snapshot: SnapshotMessage,
  localPlayer: PlayerTuple | undefined,
  veilActive: boolean,
): void {
  if (!localPlayer) {
    dom.arenaBoundary.classList.add("is-hidden");
    return;
  }

  const context = dom.arenaMinimap.getContext("2d");
  if (context) {
    const width = dom.arenaMinimap.width;
    const height = dom.arenaMinimap.height;
    const padding = 8;
    const mapWidth = width - padding * 2;
    const mapHeight = height - padding * 2;
    const [arenaX, arenaY, arenaWidth, arenaHeight] = snapshot.arena;
    const mapX = (worldX: number) =>
      padding + ((worldX - arenaX) / arenaWidth) * mapWidth;
    const mapY = (worldY: number) =>
      padding + ((worldY - arenaY) / arenaHeight) * mapHeight;

    context.clearRect(0, 0, width, height);
    context.fillStyle = "rgba(10, 8, 20, 0.94)";
    context.fillRect(0, 0, width, height);
    context.fillStyle = "rgba(75, 56, 105, 0.2)";
    context.fillRect(padding, padding, mapWidth, mapHeight);
    context.strokeStyle = "rgba(244, 188, 98, 0.82)";
    context.lineWidth = 2;
    context.strokeRect(padding, padding, mapWidth, mapHeight);
    context.strokeStyle = "rgba(141, 99, 232, 0.34)";
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(mapX(arenaX + arenaWidth / 2), padding);
    context.lineTo(mapX(arenaX + arenaWidth / 2), height - padding);
    context.moveTo(padding, mapY(arenaY + arenaHeight / 2));
    context.lineTo(width - padding, mapY(arenaY + arenaHeight / 2));
    context.stroke();

    const camera = (game.scene.getScene("vault") as VaultScene).cameras.main;
    context.strokeStyle = "rgba(255, 255, 255, 0.72)";
    context.lineWidth = 1;
    context.strokeRect(
      mapX(camera.worldView.x),
      mapY(camera.worldView.y),
      (VIEWPORT_WIDTH / arenaWidth) * mapWidth,
      (VIEWPORT_HEIGHT / arenaHeight) * mapHeight,
    );

    context.fillStyle = "#ffd66b";
    context.beginPath();
    context.arc(mapX(ARENA_WIDTH / 2), mapY(ARENA_HEIGHT / 2), 3, 0, Math.PI * 2);
    context.fill();

    if (snapshot.teamCombo) {
      const [x, y, size] = snapshot.teamCombo;
      context.strokeStyle = "#69e7ff";
      context.lineWidth = 1.5;
      context.strokeRect(
        mapX(x - size / 2),
        mapY(y - size / 2),
        (size / arenaWidth) * mapWidth,
        (size / arenaHeight) * mapHeight,
      );
    }

    for (const [x, y] of snapshot.triangleRally?.[0] ?? []) {
      context.fillStyle = "#ff8fd1";
      context.fillRect(mapX(x) - 2, mapY(y) - 2, 4, 4);
    }

    for (const enemy of snapshot.enemies) {
      const kind = enemy[1];
      if (
        kind !== "boss" &&
        kind !== "guardian" &&
        kind !== "gelatinous" &&
        kind !== "miniboss" &&
        !isNamedEnemy(kind)
      ) {
        continue;
      }
      context.fillStyle =
        kind === "gelatinous"
          ? "#69f28b"
          : kind === "boss"
            ? "#ff4e69"
            : kind === "guardian"
              ? "#ffd66b"
              : "#d58cff";
      context.beginPath();
      context.arc(
        mapX(enemy[2]),
        mapY(enemy[3]),
        kind === "boss" || kind === "gelatinous" ? 4 : 2.5,
        0,
        Math.PI * 2,
      );
      context.fill();
    }

    for (const player of snapshot.players) {
      context.fillStyle =
        player[0] === connection.localId
          ? "#fff1a6"
          : player[8]
            ? "rgba(190, 180, 210, 0.45)"
            : "rgba(105, 231, 255, 0.78)";
      context.beginPath();
      context.arc(
        mapX(player[2]),
        mapY(player[3]),
        player[0] === connection.localId ? 3.6 : 1.5,
        0,
        Math.PI * 2,
      );
      context.fill();
    }

    if (veilActive) {
      context.fillStyle = "rgba(0, 0, 0, 0.985)";
      context.fillRect(padding, padding, mapWidth, mapHeight);
      context.strokeStyle = "rgba(157, 148, 200, 0.9)";
      context.lineWidth = 2;
      context.strokeRect(padding, padding, mapWidth, mapHeight);
      context.fillStyle = "#d8d2f0";
      context.font = "700 15px 'DM Mono', monospace";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText("SIGNAL LOST", width / 2, height / 2 - 8);
      context.fillStyle = "rgba(216, 210, 240, 0.7)";
      context.font = "10px 'DM Mono', monospace";
      context.fillText("KILL VEILKEEPER", width / 2, height / 2 + 13);
    }
  }

  const [arenaX, arenaY, arenaWidth, arenaHeight] = snapshot.arena;
  const edgeDistances = [
    ["WEST", localPlayer[2] - (arenaX + 24)],
    ["EAST", arenaX + arenaWidth - 24 - localPlayer[2]],
    ["NORTH", localPlayer[3] - (arenaY + 24)],
    ["SOUTH", arenaY + arenaHeight - 24 - localPlayer[3]],
  ] as const;
  const threshold = 420;
  const sortedEdges = [...edgeDistances].sort((a, b) => a[1] - b[1]);
  const nearestDistance = Math.max(0, sortedEdges[0][1]);
  const nearbyEdges = sortedEdges
    .filter(([, distance]) => distance <= threshold && distance <= nearestDistance + 90)
    .map(([direction]) => direction);
  const edgeColor = (distance: number) => {
    const strength = Phaser.Math.Clamp(1 - Math.max(0, distance) / threshold, 0, 1);
    return `rgba(255, 84, 111, ${(strength * 0.34).toFixed(3)})`;
  };
  for (const [direction, distance] of edgeDistances) {
    dom.arenaBoundary.style.setProperty(
      "--edge-" + direction.toLowerCase(),
      edgeColor(distance),
    );
  }
  const nearBoundary = nearbyEdges.length > 0;
  dom.arenaBoundary.classList.toggle("is-hidden", !nearBoundary);
  if (nearBoundary) {
    dom.arenaBoundaryLabel.textContent =
      nearbyEdges.join(" + ") + " VAULT WALL" + (nearbyEdges.length > 1 ? "S" : "");
    dom.arenaBoundaryDistance.textContent =
      Math.ceil(nearestDistance / 10) * 10 + " units away";
  }
}

function renderPlayerHud(state: PlayerStateMessage): void {
  dom.healthMeter.style.width = percentage(state.hp / state.maxHp);
  dom.healthLabel.textContent = Math.ceil(state.hp) + "/" + state.maxHp;
  dom.xpMeter.style.width = percentage(state.xp / state.nextXp);
  dom.xpLabel.textContent = state.xp + "/" + state.nextXp;
  dom.levelLabel.textContent = String(state.level);
  dom.abilityLabel.textContent = ABILITY_NAMES[state.ability].toUpperCase() + " · SWITCH";
  dom.specialName.textContent = ULTIMATE_NAMES[state.ability].toUpperCase();
  dom.touchBomb.innerHTML =
    (state.ultimateCharges > 0
      ? "ULT ×" + state.ultimateCharges
      : "ULT") +
    "<br>" +
    escapeHtml(ULTIMATE_NAMES[state.ability]);
  dom.abilityLabel.style.setProperty(
    "--ability-color",
    "#" + ABILITY_COLORS[state.ability].toString(16).padStart(6, "0"),
  );
  dom.ghostPanel.classList.toggle("is-hidden", !state.ghost);
  dom.relicCount.textContent =
    "◇ " +
    state.artifacts.length +
    (state.artifacts.length === 1 ? " TEAM ARTIFACT" : " TEAM ARTIFACTS") +
    (latestSnapshot?.teamArtifactRounds.length
      ? " · " +
        Math.min(...latestSnapshot.teamArtifactRounds.map(([, rounds]) => rounds)) +
        "R"
      : "");
  dom.relicCount.title = (latestSnapshot?.teamArtifactRounds ?? [])
    .map(([artifact, rounds]) => ABILITY_NAMES[artifact] + " · " + rounds + " rounds left")
    .join(" + ");
  dom.relicCount.classList.toggle("is-hidden", state.artifacts.length === 0);
  dom.moteCount.textContent = state.motes + "/" + state.moteCapacity;
  const serverNow = Date.now() + connection.serverOffset;
  const challenge = state.personalChallenge;
  const challengeCompleteAge = challenge.completedAt
    ? serverNow - challenge.completedAt
    : 0;
  dom.personalChallenge.classList.remove("is-hidden");
  dom.personalChallenge.classList.toggle("complete", challenge.completed);
  dom.personalChallenge.classList.toggle(
    "departing",
    challenge.completed &&
      challenge.completedAt > 0 &&
      challengeCompleteAge >= PERSONAL_CHALLENGE_COMPLETE_DISPLAY_MS,
  );
  if (challenge.kind !== lastPersonalChallengeKind) {
    lastPersonalChallengeKind = challenge.kind;
    dom.personalChallenge.classList.remove("arriving");
    void dom.personalChallenge.offsetWidth;
    dom.personalChallenge.classList.add("arriving");
    if (challengeArrivalTimer) window.clearTimeout(challengeArrivalTimer);
    challengeArrivalTimer = window.setTimeout(() => {
      dom.personalChallenge.classList.remove("arriving");
      challengeArrivalTimer = null;
    }, 650);
  }
  dom.challengeName.textContent = challenge.name.toUpperCase();
  dom.challengeCopy.textContent = challenge.completed
    ? "Complete · rare 4LOOT awarded · new challenge incoming"
    : challenge.description;
  dom.challengeStatus.textContent =
    Math.floor(challenge.progress) + "/" + challenge.goal;
  dom.challengeMeter.style.width = percentage(
    challenge.progress / Math.max(1, challenge.goal),
  );
  dom.evolutionChip.classList.toggle("is-hidden", !state.evolution);
  dom.evolutionChip.textContent = state.evolution
    ? "EVOLVED · " + state.evolution.name.toUpperCase()
    : "";
  dom.evolutionChip.title = state.evolution?.description ?? "";
  const statuses: string[] = [];
  if (state.ultimateCharges > 0) {
    statuses.push("ULTIMATE BARRAGE ×" + state.ultimateCharges);
  }
  if (state.rodUntil > serverNow) {
    statuses.push("ROD MODE " + ((state.rodUntil - serverNow) / 1_000).toFixed(1) + "s");
  } else {
    if (state.furyUntil > serverNow) statuses.push("FURY");
    if (state.hasteUntil > serverNow) statuses.push("HASTE");
    if (state.shieldUntil > serverNow) statuses.push("SHIELD");
  }
  dom.powerupChip.classList.toggle("is-hidden", statuses.length === 0);
  dom.powerupChip.textContent = statuses.join(" · ");
  dom.powerupChip.classList.toggle("rod", state.rodUntil > serverNow);
  dom.powerupChip.classList.toggle(
    "barrage",
    state.ultimateCharges > 0,
  );
  if (latestSnapshot) renderRaidUltimate(latestSnapshot);
  updateSpecialHud();
  if (state.ghost) updateRespawnTimer();
}

function renderRaidUltimate(snapshot: SnapshotMessage): void {
  const [charge, ready, votes, requiredVotes] = snapshot.raidUltimate;
  dom.raidUltimate.classList.toggle("is-hidden", snapshot.stage === "results");
  dom.raidUltimate.classList.toggle("ready", ready);
  dom.raidUltimateMeter.style.width = percentage(charge / 100);
  dom.raidUltimateLabel.textContent = ready
    ? votes + "/" + requiredVotes + " VOTES"
    : Math.floor(charge) + "%";
  dom.raidUltimateButton.disabled = !ready;
  dom.raidUltimateButton.classList.toggle(
    "voted",
    ready && playerState?.raidUltimateVoted === true,
  );
  dom.raidUltimateButton.textContent = !ready
    ? "CHARGE THE HOARD"
    : playerState?.raidUltimateVoted
      ? "FIRE VOTE ✓"
      : "VOTE: FIRE";
}

function updateSpecialHud(): void {
  if (!playerState) return;
  const remaining = Math.max(
    0,
    playerState.specialReadyAt - (Date.now() + connection.serverOffset),
  );
  const ready = remaining === 0 && !playerState.ghost;
  dom.specialChip.classList.toggle("ready", ready);
  dom.specialCooldown.textContent = ready
    ? playerState.ultimateCharges > 0
      ? "×" + playerState.ultimateCharges + " READY"
      : "READY"
    : playerState.ghost
      ? "UNDEAD"
      : (remaining / 1_000).toFixed(1) + "s";
}

function renderTeamBuff(element: HTMLElement, stacks: number): void {
  const counter = element.querySelector("i");
  if (counter) counter.textContent = "×" + stacks;
  element.classList.toggle("inactive", stacks === 0);
}

function castVote(id: LootId): void {
  if (!playerState || latestSnapshot?.roundResults?.result !== "victory") return;
  connection.send({ t: "vote", id });
  playerState.resultVote = id;
  playerState.ready = false;
  if (latestSnapshot) renderRoundResults(latestSnapshot, true);
}

function renderRoundResults(snapshot: SnapshotMessage, force = false): void {
  const results = snapshot.roundResults;
  if (!results) return;
  const remaining = Math.max(
    0,
    results.endsAt - (Date.now() + connection.serverOffset),
  );
  dom.resultsCountdown.textContent = String(Math.ceil(remaining / 1_000));
  dom.readyCount.textContent = results.readyCount + "/" + results.playerCount;
  dom.resultRound.textContent = String(results.round);
  dom.resultTitle.textContent =
    results.result === "victory" ? "THE VAULT OPENS" : "GAME OVER";
  dom.resultCopy.textContent =
    results.result === "victory"
      ? "Your levels carry forward. The majority artifact equips to everyone for the next 3 rounds."
      : "The campaign ends here. The next descent restarts the raid at level 1.";
  dom.artifactVotePanel.classList.toggle("is-hidden", results.result !== "victory");
  dom.readyButton.disabled =
    results.result === "victory" && !playerState?.resultVote;
  dom.readyButton.classList.toggle("ready", playerState?.ready === true);
  dom.readyButton.textContent = playerState?.ready ? "READY ✓" : "READY UP";

  const signature = JSON.stringify([
    results.result,
    results.leaderboard,
    results.medals,
    results.artifactVotes.map(({ option, votes, voters }) => [
      option.id,
      votes,
      voters.map(([id]) => id),
    ]),
    playerState?.resultVote,
    playerState?.ready,
  ]);
  if (!force && signature === lastResultsSignature) return;
  lastResultsSignature = signature;

  dom.leaderboardRows.replaceChildren(
    ...results.leaderboard.map((entry, index) => {
      const [id, name, ability, damage, kills, revives, motes, level] = entry;
      const row = document.createElement("div");
      row.className = "leaderboard-row" + (id === connection.localId ? " self" : "");
      row.innerHTML =
        "<span class=\"looter-name\">" +
        (index + 1) +
        " · " +
        escapeHtml(abilityGlyph(ability) + " " + name) +
        " <small>LV" +
        level +
        "</small></span><span>" +
        compactNumber(damage) +
        "</span><span>" +
        kills +
        "</span><span>" +
        revives +
        "</span><span>" +
        motes +
        "</span>";
      return row;
    }),
  );

  dom.roundMedals.replaceChildren(
    ...results.medals.map(([id, icon, title, winnerId, winnerName, value]) => {
      const medal = document.createElement("div");
      medal.className =
        "round-medal medal-" +
        id +
        (winnerId === connection.localId ? " self" : "");
      medal.innerHTML =
        "<span>" +
        escapeHtml(icon) +
        "</span><div><b>" +
        escapeHtml(title) +
        "</b><strong>" +
        escapeHtml(winnerName) +
        "</strong><small>" +
        escapeHtml(value) +
        "</small></div>";
      return medal;
    }),
  );

  dom.artifactVoteCards.replaceChildren(
    ...results.artifactVotes.map(({ option, votes, voters }, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className =
        "artifact-vote-card" +
        (playerState?.resultVote === option.id ? " selected" : "");
      const voterNames = voters
        .map(([, name, ability]) => abilityGlyph(ability) + " " + name)
        .join(", ");
      button.title = voterNames || "No votes yet";
      button.innerHTML =
        "<span class=\"vote-icon\">" +
        escapeHtml(option.icon) +
        "</span><span><b>" +
        (index + 1) +
        " · " +
        escapeHtml(option.name) +
        "</b><small>" +
        escapeHtml(option.description) +
        "</small><span class=\"vote-voters\">" +
        escapeHtml(voterNames || "Waiting for votes…") +
        "</span></span><span class=\"vote-count\">" +
        votes +
        "</span>";
      button.addEventListener("click", () => castVote(option.id));
      return button;
    }),
  );
}

function compactNumber(value: number): string {
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + "m";
  if (value >= 10_000) return Math.round(value / 1_000) + "k";
  if (value >= 1_000) return (value / 1_000).toFixed(1) + "k";
  return String(value);
}

function showLoot(message: LootMessage): void {
  if (
    currentLoot?.expiresAt === message.expiresAt &&
    currentLoot.source === message.source
  ) {
    return;
  }
  hideClassPanel();
  currentLoot = message;
  dom.lootSource.textContent =
    message.source === "boss"
      ? "YOUR PERSONAL ARTIFACT · ONLY YOU RECEIVE IT"
      : message.source === "cube"
        ? "GELATINOUS HOARD · BONUS PICK"
      : message.source === "challenge"
        ? "CHALLENGE COMPLETE · GUARANTEED RARE"
      : message.source === "grave"
      ? "THE GRAVE MARKET"
      : message.source === "elite"
        ? "ELITE HOARD"
        : "LEVEL UP";
  dom.lootCards.replaceChildren(
    ...message.options.map((option, index) => {
      const button = document.createElement("button");
      button.className = "loot-card " + option.rarity;
      button.title = option.name + " — " + option.description;
      button.innerHTML =
        "<span class=\"loot-key\">" +
        (index + 1) +
        "</span><div><b>" +
        escapeHtml(option.name) +
        "</b><span>" +
        escapeHtml(option.description) +
        "</span></div>" +
        (message.personal
          ? "<span class=\"personal-badge\">PERSONAL</span>"
          : "");
      button.addEventListener("click", () => chooseLoot(option));
      return button;
    }),
  );
  dom.lootPanel.classList.remove("is-hidden");
  dom.lootProtection.classList.toggle("is-hidden", message.source === "grave");
  setLootLayout(true);

  if (lootCountdown) window.clearInterval(lootCountdown);
  const update = () => {
    const remainingMs = message.expiresAt - (Date.now() + connection.serverOffset);
    const remaining = Math.max(0, Math.ceil(remainingMs / 1000));
    dom.lootTimer.textContent = String(remaining);
    if (remainingMs <= 0 && currentLoot === message) {
      chooseLoot(message.options[0], true);
    }
  };
  update();
  lootCountdown = window.setInterval(update, 250);
}

function chooseLoot(option: LootOption, automatic = false): void {
  const personalArtifact = currentLoot?.source === "boss";
  submittedLootExpiresAt = currentLoot?.expiresAt ?? 0;
  connection.send({ t: "choose", id: option.id });
  showToast(
    (automatic ? "Time's up — " : "") +
      option.name +
      (personalArtifact ? " equipped to YOU" : " acquired"),
  );
  if (personalArtifact) {
    dom.resultCopy.textContent =
      option.name + " is yours alone. Other looters are choosing their own artifacts.";
  }
  hideLoot();
  window.requestAnimationFrame(() => focusGameControls());
}

function hideLoot(): void {
  currentLoot = null;
  dom.lootPanel.classList.add("is-hidden");
  dom.lootProtection.classList.add("is-hidden");
  setLootLayout(false);
  if (lootCountdown) window.clearInterval(lootCountdown);
  lootCountdown = null;
}

function syncPendingLoot(pending: LootMessage | null | undefined): void {
  if (pending === undefined) return;
  if (pending) {
    if (pending.expiresAt !== submittedLootExpiresAt) showLoot(pending);
    return;
  }
  if (currentLoot) hideLoot();
  submittedLootExpiresAt = 0;
}

function setLootLayout(open: boolean): void {
  document.body.classList.toggle("loot-open", open);
  if (open) {
    resetTouchMovement();
  }
}

function toggleClassPanel(): void {
  if (!playerState || currentLoot) return;
  if (classPanelOpen) {
    hideClassPanel();
    return;
  }

  classPanelOpen = true;
  document.body.classList.add("class-open");
  dom.classSummary.textContent =
    "LEVEL " + playerState.level + " KEPT · XP AND ALL UPGRADES KEPT";
  for (const button of dom.classCards.querySelectorAll<HTMLButtonElement>(
    "button[data-switch-ability]",
  )) {
    const active = button.dataset.switchAbility === playerState.ability;
    button.setAttribute("aria-pressed", String(active));
    button.toggleAttribute("disabled", active);
  }
  dom.classPanel.classList.remove("is-hidden");
  setLootLayout(true);
  window.requestAnimationFrame(() => focusGameControls());
}

function hideClassPanel(): void {
  classPanelOpen = false;
  document.body.classList.remove("class-open");
  dom.classPanel.classList.add("is-hidden");
  setLootLayout(Boolean(currentLoot));
  window.requestAnimationFrame(() => focusGameControls());
}

function switchAbility(ability: AbilityId): void {
  if (!playerState || ability === playerState.ability) return;
  connection.rememberAbility(ability);
  connection.send({ t: "switch", ability });
  playerState.ability = ability;
  renderPlayerHud(playerState);
  showToast("Switched to " + ABILITY_NAMES[ability] + " — level preserved");
  hideClassPanel();
  window.requestAnimationFrame(() => focusGameControls());
}

function focusGameControls(resetKeys = false): void {
  if (!dom.join.classList.contains("is-hidden")) return;
  game.canvas.focus({ preventScroll: true });
  if (resetKeys) {
    game.scene.getScene("vault").input.keyboard?.resetKeys();
  }
}

function setupTouchControls(): void {
  let stickPointerId: number | null = null;
  const updateStick = (event: PointerEvent) => {
    const bounds = dom.touchStick.getBoundingClientRect();
    const dx = event.clientX - (bounds.left + bounds.width / 2);
    const dy = event.clientY - (bounds.top + bounds.height / 2);
    const maxDistance = bounds.width * 0.34;
    const distance = Math.hypot(dx, dy);
    const scale = distance > maxDistance ? maxDistance / distance : 1;
    const knobX = dx * scale;
    const knobY = dy * scale;
    arenaTouch.x = 0;
    arenaTouch.y = 0;
    arenaTouch.targetX = null;
    arenaTouch.targetY = null;
    mobileMovement.x = maxDistance ? knobX / maxDistance : 0;
    mobileMovement.y = maxDistance ? knobY / maxDistance : 0;
    dom.touchKnob.style.transform =
      "translate(" + knobX.toFixed(1) + "px, " + knobY.toFixed(1) + "px)";
  };
  const releaseStick = (event?: PointerEvent) => {
    if (event && stickPointerId !== event.pointerId) return;
    stickPointerId = null;
    mobileMovement.x = 0;
    mobileMovement.y = 0;
    dom.touchKnob.style.transform = "translate(0px, 0px)";
  };

  dom.touchStick.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    stickPointerId = event.pointerId;
    dom.touchStick.setPointerCapture(event.pointerId);
    updateStick(event);
  });
  dom.touchStick.addEventListener("pointermove", (event) => {
    if (stickPointerId === event.pointerId) updateStick(event);
  });
  dom.touchStick.addEventListener("pointerup", releaseStick);
  dom.touchStick.addEventListener("pointercancel", releaseStick);
  window.addEventListener("blur", resetTouchMovement);

  const canvas = game.canvas;
  const arenaPoint = (event: PointerEvent) => {
    const bounds = canvas.getBoundingClientRect();
    const viewportPoint = canvasPointToArena(
      event.clientX,
      event.clientY,
      bounds,
      VIEWPORT_WIDTH,
      VIEWPORT_HEIGHT,
    );
    const camera = (game.scene.getScene("vault") as VaultScene).cameras.main;
    return viewportPointToWorld(
      viewportPoint,
      camera.worldView.x,
      camera.worldView.y,
      ARENA_WIDTH,
      ARENA_HEIGHT,
    );
  };
  const acceptsArenaTouch = (event: PointerEvent) =>
    (event.pointerType === "touch" || event.pointerType === "pen") &&
    document.body.classList.contains("game-active") &&
    !document.body.classList.contains("loot-open") &&
    !document.body.classList.contains("class-open") &&
    !document.body.classList.contains("results-open") &&
    !playerState?.ghost;

  canvas.addEventListener("pointerdown", (event) => {
    if (!acceptsArenaTouch(event) || arenaTouch.pointerId !== null) return;
    event.preventDefault();
    const point = arenaPoint(event);
    arenaTouch.pointerId = event.pointerId;
    arenaTouch.startClientX = event.clientX;
    arenaTouch.startClientY = event.clientY;
    arenaTouch.x = 0;
    arenaTouch.y = 0;
    arenaTouch.targetX = point.x;
    arenaTouch.targetY = point.y;
    mobileMovement.x = 0;
    mobileMovement.y = 0;
    dom.touchKnob.style.transform = "translate(0px, 0px)";
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
      // Some embedded mobile browsers do not expose pointer capture.
    }
  });
  canvas.addEventListener("pointermove", (event) => {
    if (arenaTouch.pointerId !== event.pointerId) return;
    event.preventDefault();
    const dx = event.clientX - arenaTouch.startClientX;
    const dy = event.clientY - arenaTouch.startClientY;
    const direction = touchDragDirection(dx, dy);
    if (!direction) return;
    arenaTouch.targetX = null;
    arenaTouch.targetY = null;
    arenaTouch.x = direction.x;
    arenaTouch.y = direction.y;
  });
  const releaseArenaTouch = (event: PointerEvent, cancelled = false) => {
    if (arenaTouch.pointerId !== event.pointerId) return;
    const wasDragging = arenaTouch.x !== 0 || arenaTouch.y !== 0;
    arenaTouch.pointerId = null;
    arenaTouch.x = 0;
    arenaTouch.y = 0;
    if (cancelled || wasDragging) {
      arenaTouch.targetX = null;
      arenaTouch.targetY = null;
    }
  };
  canvas.addEventListener("pointerup", (event) => releaseArenaTouch(event));
  canvas.addEventListener("pointercancel", (event) => releaseArenaTouch(event, true));

  dom.touchBomb.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    (game.scene.getScene("vault") as VaultScene).fireLootBomb(true);
  });
  dom.touchDash.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    (game.scene.getScene("vault") as VaultScene).startLocalDash();
  });
}

function resetTouchMovement(): void {
  mobileMovement.x = 0;
  mobileMovement.y = 0;
  arenaTouch.pointerId = null;
  arenaTouch.x = 0;
  arenaTouch.y = 0;
  arenaTouch.targetX = null;
  arenaTouch.targetY = null;
  dom.touchKnob.style.transform = "translate(0px, 0px)";
}

function updateRespawnTimer(): void {
  if (!playerState?.ghost) return;
  const remaining = Math.max(
    0,
    Math.ceil((playerState.respawnAt - (Date.now() + connection.serverOffset)) / 1000),
  );
  dom.respawnTimer.textContent = String(remaining);
  if (remaining > 0) window.setTimeout(updateRespawnTimer, 250);
}

function showResults(result: "victory" | "defeat", text: string): void {
  hideLoot();
  if (classPanelOpen) hideClassPanel();
  resetTouchMovement();
  for (const toast of document.querySelectorAll(".toast")) toast.remove();
  dom.results.classList.remove("is-hidden");
  dom.resultTitle.textContent =
    result === "victory" ? "THE VAULT OPENS" : "GAME OVER";
  dom.resultCopy.textContent =
    text +
    (result === "victory"
      ? " Vote for the artifact the whole team carries forward."
      : " Ready up when you want to begin a fresh campaign.");
}

let raidWarningTimer: number | null = null;
let raidWarningSequence = 0;
let activeRaidWarningKind: RaidWarningMessage["kind"] | null = null;
let queuedRaidWarning: RaidWarningMessage | null = null;

function showRaidWarning(message: RaidWarningMessage): void {
  if (
    activeRaidWarningKind &&
    !dom.raidWarning.classList.contains("is-hidden") &&
    raidWarningPriority(activeRaidWarningKind) > raidWarningPriority(message.kind)
  ) {
    queuedRaidWarning = message;
    return;
  }
  raidWarningSequence += 1;
  const sequence = raidWarningSequence;
  if (raidWarningTimer) window.clearTimeout(raidWarningTimer);
  activeRaidWarningKind = message.kind;
  dom.raidWarning.dataset.kind = message.kind;
  dom.raidWarningKicker.textContent =
    message.kind === "combo"
      ? "COORDINATION CHECK"
      : message.kind === "frenzy"
        ? "FRENZY ACTIVE"
        : message.kind === "overdrive"
          ? "POWER DROP"
          : message.kind === "boss"
            ? "FINAL BOSS"
            : message.kind === "bossphase"
              ? "BOSS PHASE CHANGE"
              : message.kind === "ultimate"
                ? "RAID ULTIMATE"
            : message.kind === "cube"
              ? "LETHAL RAID BOSS"
              : message.kind === "imposter"
                ? "DECEPTION PROTOCOL"
                : message.kind === "laser"
                  ? "ARENA LASER"
                  : message.kind === "fog"
                    ? "VISION LOST"
                      : message.kind === "pools"
                        ? "AREA DENIAL"
                        : message.kind === "army"
                          ? "MOVING DAMAGE WALL"
                      : message.kind === "powerup"
                        ? "RARE POWERUP"
            : message.kind === "miniboss"
              ? "MINIBOSS INCOMING"
              : message.kind === "surge"
                ? "RAID SYNCHRONIZATION"
                : message.kind === "named"
                  ? "NAMED THREAT"
                  : "PRIORITY TARGET";
  dom.raidWarningTitle.textContent = message.title;
  dom.raidWarningCopy.textContent = message.text;
  dom.raidWarning.classList.remove("is-hidden", "show");
  void dom.raidWarning.offsetWidth;
  dom.raidWarning.classList.add("show");
  const scene = game.scene.getScene("vault") as VaultScene;
  const major =
    message.kind === "boss" ||
    message.kind === "bossphase" ||
    message.kind === "ultimate" ||
    message.kind === "cube" ||
    message.kind === "army";
  scene.cameras.main.shake(major ? 650 : 320, major ? 0.012 : 0.006);
  raidWarningTimer = window.setTimeout(() => {
    if (sequence !== raidWarningSequence) return;
    dom.raidWarning.classList.remove("show");
    window.setTimeout(() => {
      if (sequence !== raidWarningSequence) return;
      dom.raidWarning.classList.add("is-hidden");
      activeRaidWarningKind = null;
      if (queuedRaidWarning) {
        const queued = queuedRaidWarning;
        queuedRaidWarning = null;
        showRaidWarning(queued);
      }
    }, 220);
    raidWarningTimer = null;
  }, message.kind === "army" ? 2_400 : major ? 4_500 : 3_300);
}

function raidWarningPriority(kind: RaidWarningMessage["kind"]): number {
  if (
    kind === "boss" ||
    kind === "bossphase" ||
    kind === "ultimate" ||
    kind === "cube" ||
    kind === "army"
  ) {
    return 3;
  }
  if (
    kind === "guardian" ||
    kind === "overdrive" ||
    kind === "frenzy" ||
    kind === "surge"
  ) {
    return 2;
  }
  return 1;
}

function showToast(text: string): void {
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = text;
  dom.toastStack.appendChild(toast);
  while (dom.toastStack.childElementCount > 4) {
    dom.toastStack.firstElementChild?.remove();
  }
  window.setTimeout(() => toast.classList.add("show"), 20);
  window.setTimeout(() => {
    toast.classList.remove("show");
    window.setTimeout(() => toast.remove(), 300);
  }, 2_700);
}

let damageFlashTimer: number | null = null;

function flashDamage(): void {
  document.body.classList.remove("damage-flash");
  void document.body.offsetWidth;
  document.body.classList.add("damage-flash");
  if (damageFlashTimer) window.clearTimeout(damageFlashTimer);
  damageFlashTimer = window.setTimeout(() => {
    document.body.classList.remove("damage-flash");
    damageFlashTimer = null;
  }, 430);
}

function flashRaidUltimate(): void {
  document.body.classList.remove("raid-ultimate-flash");
  void document.body.offsetWidth;
  document.body.classList.add("raid-ultimate-flash");
  window.setTimeout(
    () => document.body.classList.remove("raid-ultimate-flash"),
    1_250,
  );
}

function stageName(stage: MatchStage): string {
  return {
    descent: "THE DESCENT",
    escalation: "THE VAULT STIRS",
    chaos: "GREED UNBOUND",
    boss: "THE VAULTHEART",
    results: "RUN COMPLETE",
  }[stage];
}

function formatTime(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes + ":" + String(seconds).padStart(2, "0");
}

function abilityGlyph(ability: AbilityId): string {
  return {
    ember: "✦",
    moon: "☾",
    chain: "ϟ",
    void: "◆",
    frost: "❄",
    solar: "☀",
    raven: "♠",
    necro: "☠",
    bard: "♪",
  }[ability];
}

function enemyColor(kind: EnemyTuple[1]): number {
  return {
    gnawer: 0xff5678,
    charger: 0xff934f,
    spitter: 0x77dc8b,
    brute: 0xa563e8,
    skitter: 0x6ef7ff,
    bulwark: 0xd79b3f,
    stalker: 0xff477d,
    hexweaver: 0xb74cff,
    imposter: 0xff244f,
    arcspinner: 0x2dd7e8,
    veilkeeper: 0x504a72,
    mirecaller: 0x9d45bd,
    gravemaw: 0xc45535,
    nullbell: 0x756e9f,
    shardmother: 0x257f9e,
    gelatinous: 0x58d85d,
    weakpoint: 0xffd34f,
    miniboss: 0xc55cff,
    guardian: 0x27bda0,
    boss: 0x8d245e,
  }[kind];
}

function isNamedEnemy(
  kind: EnemyTuple[1],
): kind is
  | "bulwark"
  | "stalker"
  | "hexweaver"
  | "imposter"
  | "arcspinner"
  | "veilkeeper"
  | "mirecaller"
  | "gravemaw"
  | "nullbell"
  | "shardmother" {
  return (
    kind === "bulwark" ||
    kind === "stalker" ||
    kind === "hexweaver" ||
    kind === "imposter" ||
    kind === "arcspinner" ||
    kind === "veilkeeper" ||
    kind === "mirecaller" ||
    kind === "gravemaw" ||
    kind === "nullbell" ||
    kind === "shardmother"
  );
}

function namedEnemyName(
  kind:
    | "bulwark"
    | "stalker"
    | "hexweaver"
    | "imposter"
    | "arcspinner"
    | "veilkeeper"
    | "mirecaller"
    | "gravemaw"
    | "nullbell"
    | "shardmother",
): string {
  return {
    bulwark: "GOLDMAW BULWARK",
    stalker: "BLINKFANG STALKER",
    hexweaver: "RIFT HEXWEAVER",
    imposter: "IMPOSTER",
    arcspinner: "ARCSPINNER CONDUCTOR",
    veilkeeper: "VEILKEEPER",
    mirecaller: "MIRECALLER",
    gravemaw: "GRAVEMAW ANCHOR",
    nullbell: "NULLBELL SENTINEL",
    shardmother: "SHARDMOTHER",
  }[kind];
}

function powerUpColor(kind: PowerUpTuple[1]): number {
  return {
    fury: 0xff515b,
    haste: 0x5feaff,
    shield: 0x82a7ff,
    nova: 0xffd65a,
    truesight: 0xe78cff,
    barrage: 0xff62df,
    rod: 0xff7b36,
  }[kind];
}

function powerUpGlyph(kind: PowerUpTuple[1]): string {
  return {
    fury: "⚔",
    haste: "»",
    shield: "⬡",
    nova: "✹",
    truesight: "◉",
    barrage: "Ⅵ",
    rod: "R",
  }[kind];
}

function destroyPlayerView(view: PlayerView): void {
  view.body.destroy();
  view.outline.destroy();
  view.name.destroy();
  view.evolution.destroy();
  view.level.destroy();
  view.rune.destroy();
  view.revive.destroy();
}

function destroyEnemyView(view: EnemyView): void {
  view.body.destroy();
  view.healthBg.destroy();
  view.health.destroy();
  view.label?.destroy();
  view.rune?.destroy();
}

function destroySummonView(view: SummonView): void {
  view.body.destroy();
  view.outline.destroy();
  view.rune.destroy();
}

function percentage(value: number): string {
  return Math.round(Phaser.Math.Clamp(value, 0, 1) * 100) + "%";
}

function randomName(): string {
  const first = ["Tiny", "Lucky", "Cursed", "Greedy", "Sneaky", "Brave"];
  const second = ["Goblin", "Moth", "Menace", "Bandit", "Mimic", "Rook"];
  return (
    first[Math.floor(Math.random() * first.length)] +
    " " +
    second[Math.floor(Math.random() * second.length)]
  );
}

function escapeHtml(value: string): string {
  const element = document.createElement("span");
  element.textContent = value;
  return element.innerHTML;
}

function required<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error("Missing #" + id);
  return element as T;
}

setupTouchControls();
void game;
